/**
 * Finance.jsx — Nirvikalp 4.0 Finance & Accounting Module v2
 * NEW: DB persistence (no more browser loss), AI Document Scanner (PO/LC/Receipt),
 * CSV Employee Import, Invoice enforced via Final Inspection PASS qty,
 * FOB from Style Passport, Shipped qty capped at FI-passed qty.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';

const fmt  = (n, d=0) => (parseFloat(n)||0).toLocaleString('en-IN', {minimumFractionDigits:d,maximumFractionDigits:d});
const fmtC = (n) => `USD ${fmt(n,2)}`;
const today = () => new Date().toISOString().split('T')[0];

const EXPENSE_CATS = [
  'Fabric & Materials','Trims & Accessories','Direct Labour','Packaging',
  'Factory Overhead','Salaries – Production','Salaries – Admin',
  'Transport & Freight','Bank Charges','Commission','Depreciation',
  'Loan Interest','Rent & Utilities','Marketing','Maintenance','Taxation','Miscellaneous',
];

const S = {
  card: (b='#e6e9ef') => ({background:'#fff',border:`1px solid ${b}`,borderRadius:'8px',padding:'20px',marginBottom:'20px',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}),
  hdr: {fontSize:'0.95rem',fontWeight:'bold',color:'#323338',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'16px',marginTop:0,display:'flex',justifyContent:'space-between',alignItems:'center'},
  inp: {width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #c5c7d4',fontSize:'0.88rem',color:'#323338',boxSizing:'border-box'},
  sel: {width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #c5c7d4',fontSize:'0.88rem',color:'#323338',background:'#fff',boxSizing:'border-box'},
  btn: (bg='#0073ea',fg='#fff') => ({padding:'8px 14px',background:bg,color:fg,border:'none',borderRadius:'6px',fontWeight:'bold',cursor:'pointer',fontSize:'0.82rem',whiteSpace:'nowrap'}),
  TH: {textAlign:'left',padding:'9px 12px',borderBottom:'2px solid #e6e9ef',color:'#676879',fontSize:'0.72rem',textTransform:'uppercase',fontWeight:'700',background:'#f0f1f4',whiteSpace:'nowrap'},
  TD: {padding:'9px 12px',borderBottom:'1px solid #f0f1f4',fontSize:'0.84rem',color:'#323338',verticalAlign:'middle'},
  tab: (a) => ({padding:'9px 14px',border:'none',background:'transparent',cursor:'pointer',fontSize:'0.83rem',whiteSpace:'nowrap',color:a?'#0073ea':'#676879',borderBottom:a?'3px solid #0073ea':'3px solid transparent',fontWeight:a?'bold':'normal'}),
  grid: (cols) => ({display:'grid',gridTemplateColumns:cols,gap:'14px'}),
  kpi: (c='#0073ea') => ({background:'#fff',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',borderLeft:`4px solid ${c}`,boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}),
};

const Fld = memo(({label,children,span=1}) => (
  <div style={{gridColumn:`span ${span}`}}>
    <div style={{fontSize:'0.68rem',fontWeight:'bold',color:'#676879',textTransform:'uppercase',marginBottom:'4px'}}>{label}</div>
    {children}
  </div>
));

const MsgBar = memo(({msg,type='ok'}) => msg ? (
  <div style={{background:type==='ok'?'#f0fdf4':'#fef2f2',border:`1px solid ${type==='ok'?'#86efac':'#fca5a5'}`,borderRadius:'6px',padding:'8px 14px',marginBottom:'12px',fontSize:'0.8rem',color:type==='ok'?'#166534':'#991b1b'}}>{msg}</div>
) : null);

const calcDep = (a) => {
  const cost=parseFloat(a.cost)||0,salvage=parseFloat(a.salvage)||0,life=parseInt(a.useful_life)||5;
  const yrs=(new Date()-new Date(a.purchase_date))/(365*24*3600*1000);
  if(a.dep_method==='SLM'){const ann=(cost-salvage)/life;const acc=Math.min(ann*yrs,cost-salvage);return{annual:ann,accumulated:acc,bookValue:cost-acc};}
  const rate=1-Math.pow(salvage/Math.max(cost,1),1/life);const bv=cost*Math.pow(1-rate,yrs);
  return{annual:bv*rate,accumulated:cost-bv,bookValue:Math.max(bv,0)};
};
const calcEMI=(p,r,m)=>{const rm=r/12/100;if(rm===0)return p/m;return p*rm*Math.pow(1+rm,m)/(Math.pow(1+rm,m)-1);};
// Safety: always return an array even if DB returns unexpected shape
const arr = (v) => Array.isArray(v) ? v : [];

// ═══════════════════════════════════════════════════════════════════════════════
export default function Finance({ db, saveToDB }) {
  const [tab, setTab] = useState('dashboard');
  const [fin, setFin] = useState({invoices:[],receipts:[],lcs:[],salesContracts:[],expenses:[],salaries:[],assets:[],loans:[],taxEntries:[],miscIncome:[],bankStatements:[],budgets:[],paymentRequests:[]});
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    const s = db?.financeData;
    if(!s || typeof s !== 'object') return;
    // DB stores each section as { sectionName: [...] } blob.
    // After decode, financeData.invoices might be { invoices:[...] } instead of [...].
    // Unwrap and force-coerce every section to an array.
    const SECTIONS = ['invoices','receipts','lcs','salesContracts','expenses','salaries','assets','loans','taxEntries','miscIncome','bankStatements','budgets','paymentRequests'];
    const clean = {};
    SECTIONS.forEach(key => {
      let val = s[key];
      // Unwrap if the DB blob wrapped it: { invoices: [...] }
      if (val && !Array.isArray(val) && typeof val === 'object' && Array.isArray(val[key])) val = val[key];
      // Still not an array? Try other common wrapping patterns
      if (val && !Array.isArray(val) && typeof val === 'object') {
        const inner = Object.values(val).find(v => Array.isArray(v));
        if (inner) val = inner;
      }
      clean[key] = Array.isArray(val) ? val : [];
    });
    setFin(prev => ({ ...prev, ...clean }));
  }, [db]);

  const showFlash = useCallback((msg,type='ok') => { setFlash({msg,type}); setTimeout(()=>setFlash(null),3500); },[]);

  const saveSection = useCallback(async (key,list) => {
    setSaving(true);
    try {
      const r = await saveToDB('finance',{type:'FINANCE_DATA',section:key,record_key:'main',[key]:list});
      if(!r?.success) showFlash('Save failed: '+(r?.error||'unknown'),'err');
    } catch(e){ showFlash('DB error: '+e.message,'err'); }
    finally { setSaving(false); }
  },[saveToDB,showFlash]);

  // Stable upd factory — memoized so child components don't re-render needlessly
  const upd = useCallback((key) => (list) => {
    setFin(prev => ({ ...prev, [key]:list }));
    saveSection(key, list);
  }, [saveSection]);

  const pl = useMemo(()=>{
    const exportSales=arr(fin.invoices).reduce((s,i)=>s+(parseFloat(i.amount_usd)||0),0);
    const miscInc=arr(fin.miscIncome).reduce((s,m)=>s+(parseFloat(m.amount)||0),0);
    const poRev=(db?.mgmtStyles||[]).reduce((s,st)=>{
      const shipped=(db?.productionLogs||[]).filter(l=>l.style===st.styleRef&&l.type==='SHIP').reduce((a,l)=>a+(parseInt(l.quantity)||0),0);
      const fob=parseFloat(st.fobPrice||st.costing?.fob||st.pos?.[0]?.fob||0);
      return s+(shipped*fob);
    },0);
    const totalRevenue=Math.max(exportSales||poRev,poRev)+miscInc;
    // Single pass over expenses for COGS + OPEX
    const COGS_CATS = new Set(['Fabric & Materials','Trims & Accessories','Direct Labour','Packaging','Factory Overhead']);
    const OPEX_CATS = new Set(['Salaries – Admin','Transport & Freight','Bank Charges','Commission','Rent & Utilities','Marketing','Maintenance','Miscellaneous']);
    let cogs=0, opexBase=0;
    arr(fin.expenses).forEach(e=>{
      const a=parseFloat(e.amount)||0;
      if(COGS_CATS.has(e.category)) cogs+=a;
      else if(OPEX_CATS.has(e.category)) opexBase+=a;
    });
    const salTotal=arr(fin.salaries).reduce((s,r)=>s+(parseFloat(r.net)||0),0);
    const opex=opexBase+salTotal;
    const depn=arr(fin.assets).reduce((s,a)=>s+(calcDep(a).annual||0),0);
    const loanInt=arr(fin.loans).reduce((s,l)=>s+(parseFloat(l.amount||0)*(parseFloat(l.rate||0)/100/12)),0)*12;
    const taxPaid=arr(fin.taxEntries).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);
    const grossProfit=totalRevenue-cogs,ebit=grossProfit-opex-depn-loanInt,netProfit=ebit-taxPaid;
    return{totalRevenue,cogs,grossProfit,opex,depn,loanInt,ebit,taxPaid,netProfit,exportSales:exportSales||poRev,miscInc,gpMargin:totalRevenue>0?(grossProfit/totalRevenue*100):0,npMargin:totalRevenue>0?(netProfit/totalRevenue*100):0};
  },[fin,db]);

  const rcv=useMemo(()=>{
    const inv=arr(fin.invoices).reduce((s,i)=>s+(parseFloat(i.amount_usd)||0),0);
    const rec=arr(fin.receipts).reduce((s,r)=>s+(parseFloat(r.amount)||0),0);
    return{invoiced:inv,received:rec,outstanding:inv-rec};
  },[fin]);

  const TABS=[
    {id:'dashboard',label:'📊 Dashboard'},{id:'receivables',label:'💵 Sales & Receivables'},
    {id:'expenses',label:'💸 Expenses'},{id:'payroll',label:'👥 Payroll'},
    {id:'assets',label:'🏭 Assets'},{id:'loans',label:'🏦 Loans'},
    {id:'taxation',label:'📋 Taxation'},{id:'banking',label:'🔁 Bank AI'},
    {id:'budget',label:'💼 Budget & Control'},{id:'documents',label:'📤 AI Doc Scanner'},{id:'reports',label:'📄 P&L Reports'},
  ];

  return (
    <div style={{fontFamily:'Inter,sans-serif',background:'#f0f1f4',minHeight:'100%'}}>
      <div style={{background:'#1e1f2e',padding:'0 20px',height:'56px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:'1rem',fontWeight:'900',color:'#fff'}}>💰 FINANCE & ACCOUNTS</div>
          <div style={{fontSize:'0.7rem',color:'#93c5fd'}}>P&L · Receivables · Expenses · Assets · AI Doc Extraction</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'14px'}}>
          {saving&&<span style={{fontSize:'0.7rem',color:'#fbbf24'}}>⟳ Saving to DB…</span>}
          {flash&&<span style={{fontSize:'0.7rem',padding:'4px 10px',background:'rgba(255,255,255,0.12)',borderRadius:'6px',color:flash.type==='ok'?'#4ade80':'#f87171'}}>{flash.msg}</span>}
          <div style={{color:'#93c5fd',fontSize:'0.72rem'}}>Net: <strong style={{color:pl.netProfit>=0?'#4ade80':'#f87171',fontSize:'0.95rem'}}>{fmtC(pl.netProfit)}</strong></div>
        </div>
      </div>
      <div style={{background:'#fff',borderBottom:'1px solid #e6e9ef',padding:'0 16px',display:'flex',overflowX:'auto'}}>
        {TABS.map(t=><button key={t.id} style={S.tab(tab===t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>)}
      </div>
      <div style={{padding:'18px'}}>
        {tab==='dashboard'   && <FinDash pl={pl} fin={fin} rcv={rcv} db={db}/>}
        {tab==='receivables' && <Receivables fin={fin} upd={upd} db={db} flash={showFlash}/>}
        {tab==='expenses'    && <Expenses fin={fin} upd={upd}/>}
        {tab==='payroll'     && <Payroll fin={fin} upd={upd}/>}
        {tab==='assets'      && <Assets fin={fin} upd={upd}/>}
        {tab==='loans'       && <Loans fin={fin} upd={upd}/>}
        {tab==='taxation'    && <Taxation fin={fin} upd={upd} pl={pl}/>}
        {tab==='banking'     && <Banking fin={fin} upd={upd}/>}
        {tab==='budget'      && <Budget fin={fin} upd={upd}/>}
        {tab==='documents'   && <AIDocScanner fin={fin} upd={upd} db={db} flash={showFlash}/>}
        {tab==='reports'     && <Reports pl={pl} fin={fin}/>}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function FinDash({pl,fin,rcv,db}){
  const kpis=[
    {l:'Total Revenue',v:fmtC(pl.totalRevenue),c:'#00a65e',sub:`GP: ${fmt(pl.gpMargin,1)}%`},
    {l:'Gross Profit',v:fmtC(pl.grossProfit),c:'#0073ea',sub:`Margin: ${fmt(pl.gpMargin,1)}%`},
    {l:'Net Profit',v:fmtC(pl.netProfit),c:pl.netProfit>=0?'#16a34a':'#e2445c',sub:`NP: ${fmt(pl.npMargin,1)}%`},
    {l:'Total Expenses',v:fmtC(pl.cogs+pl.opex),c:'#e2445c',sub:`COGS: ${fmtC(pl.cogs)}`},
    {l:'Receivables',v:fmtC(rcv.invoiced),c:'#7c3aed',sub:`Outstanding: ${fmtC(rcv.outstanding)}`},
    {l:'Received',v:fmtC(rcv.received),c:'#0073ea',sub:`${arr(fin.receipts).length} payments`},
  ];
  return (
    <>
      <div style={S.grid('repeat(3,1fr)')}>
        {kpis.map(k=>(
          <div key={k.l} style={S.kpi(k.c)}>
            <div style={{fontSize:'0.68rem',fontWeight:'bold',color:'#676879',textTransform:'uppercase'}}>{k.l}</div>
            <div style={{fontSize:'1.3rem',fontWeight:'900',color:k.c,margin:'4px 0'}}>{k.v}</div>
            <div style={{fontSize:'0.68rem',color:'#c5c7d4'}}>{k.sub}</div>
          </div>
        ))}
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>📊 P&L AT A GLANCE</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
          <div>
            {[['Revenue',pl.totalRevenue,'#00a65e'],['− COGS',-pl.cogs,'#e2445c'],['= Gross Profit',pl.grossProfit,'#0073ea'],
              ['− OpEx',-pl.opex,'#e2445c'],['− Depreciation',-pl.depn,'#f59e0b'],['− Loan Interest',-pl.loanInt,'#f59e0b'],
              ['= EBIT',pl.ebit,'#7c3aed'],['− Tax',-pl.taxPaid,'#e2445c'],['= Net Profit',pl.netProfit,pl.netProfit>=0?'#16a34a':'#e2445c'],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #f1f5f9',fontWeight:l.startsWith('=')?'800':'400'}}>
                <span style={{fontSize:'0.82rem'}}>{l}</span>
                <span style={{fontSize:'0.82rem',fontWeight:'bold',color:c}}>{fmtC(Math.abs(v))}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{fontWeight:'bold',fontSize:'0.75rem',color:'#0073ea',marginBottom:'10px'}}>RECEIVABLES AGEING</div>
            {[0,30,60,90].map(days=>{
              const cutoff=new Date();cutoff.setDate(cutoff.getDate()-days);
              const next=new Date();next.setDate(next.getDate()-(days+30));
              const amt=arr(fin.invoices).filter(inv=>{ const d=new Date(inv.due_date); return days===0?d>=cutoff:d<cutoff&&d>=next; }).reduce((s,i)=>s+(parseFloat(i.amount_usd)||0),0);
              const col=days===0?'#00a65e':days<=30?'#f59e0b':days<=60?'#ea580c':'#e2445c';
              return(
                <div key={days} style={{display:'flex',justifyContent:'space-between',padding:'5px 10px',background:`${col}11`,borderRadius:'6px',marginBottom:'5px',border:`1px solid ${col}33`}}>
                  <span style={{fontSize:'0.78rem'}}>{days===0?'Current':`${days}-${days+30}d`}</span>
                  <span style={{fontWeight:'bold',color:col}}>{fmtC(amt)}</span>
                </div>
              );
            })}
            <div style={{marginTop:'8px',padding:'8px 10px',background:'#f0f1f4',borderRadius:'6px',display:'flex',justifyContent:'space-between'}}>
              <span style={{fontSize:'0.78rem',fontWeight:'bold'}}>Total Outstanding</span>
              <span style={{fontWeight:'900',color:'#7c3aed'}}>{fmtC(rcv.outstanding)}</span>
            </div>
          </div>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
        {[['Recent Receipts',arr(fin.receipts).slice(-5).reverse(),'date','reference','amount','#00a65e'],
          ['Recent Expenses',arr(fin.expenses).slice(-5).reverse(),'date','description','amount','#e2445c']].map(([t,items,d,desc,amt,c])=>(
          <div key={t} style={S.card()}>
            <div style={S.hdr}>{t}</div>
            {items.map((r,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #f8fafc',fontSize:'0.78rem'}}>
                <span>{r[d]} · {String(r[desc]||'').slice(0,25)}</span>
                <strong style={{color:c}}>{fmtC(r[amt])}</strong>
              </div>
            ))}
            {!items.length&&<div style={{color:'#c5c7d4',fontSize:'0.78rem'}}>No records yet</div>}
          </div>
        ))}
      </div>
    </>
  );
}

// ── SALES & RECEIVABLES ───────────────────────────────────────────────────────
function Receivables({fin,upd,db,flash}){
  const [sub,setSub]=useState('invoices');
  const [form,setForm]=useState({});
  const [fiMsg,setFiMsg]=useState('');
  const F=(k)=>form[k]||'';
  const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const addItem=(key,item)=>{ upd(key)([...(fin[key]||[]),{id:Date.now(),created:today(),...item}]); flash(`${key} saved ✓`); };
  const delItem=(key,id)=>upd(key)((fin[key]||[]).filter(i=>i.id!==id));

  // PO options from poSizeTable (Style Passport)
  const poOpts=useMemo(()=>{
    const seen=new Set(),pos=[];
    (db?.poSizeTable||[]).forEach(r=>{
      const k=`${r.style_ref}||${r.po_no}`;
      if(!seen.has(k)){seen.add(k);pos.push({label:`${r.style_ref} / ${r.po_no}`,styleRef:r.style_ref,po:r.po_no,fob:parseFloat(r.fob||0)});}
    });
    return pos;
  },[db]);

  // Final Inspection PASS qty (Annexure 68 FINAL only)
  const getFiPassQty=useCallback((styleRef,poNo)=>{
    return (db?.qcDeptLogs||[])
      .filter(l=>l.formType==='FINAL'&&l.result==='PASS'&&(l.style===styleRef||(poNo&&l.po_no===poNo)))
      .reduce((s,l)=>s+(parseInt(l.totalChecked)||0),0);
  },[db]);

  const getInvoicedQty=useCallback((poRef)=>
    arr(fin.invoices).filter(i=>i.po_ref===poRef).reduce((s,i)=>s+(parseInt(i.qty)||0),0)
  ,[fin.invoices]);

  const loadFromFI=()=>{
    if(!F('po_ref')){setFiMsg('Select a PO first.');return;}
    const sel=poOpts.find(p=>p.po===F('po_ref'));
    if(!sel)return;
    const fiQty=getFiPassQty(sel.styleRef,sel.po);
    if(fiQty<=0){setFiMsg('⚠ No FINAL PASS inspection found for this PO. Cannot create invoice.');return;}
    const already=getInvoicedQty(sel.po);
    const avail=fiQty-already;
    if(avail<=0){setFiMsg(`All FI-passed qty (${fiQty} pcs) already invoiced.`);return;}
    setForm(p=>({...p,qty:avail,unit_price:sel.fob,amount_usd:(avail*sel.fob).toFixed(2),buyer:sel.styleRef,_fi_pass_qty:fiQty,_fi_locked:true}));
    setFiMsg(`✓ FI PASS: ${fiQty} pcs. Invoiced: ${already}. Available: ${avail} pcs @ FOB ${fmtC(sel.fob)}`);
  };

  const addInvoice=()=>{
    const qty=parseInt(F('qty')||0);
    const sel=poOpts.find(p=>p.po===F('po_ref'));
    if(sel){
      const fiQty=getFiPassQty(sel.styleRef,sel.po);
      if(fiQty>0&&qty>(fiQty-getInvoicedQty(sel.po))){
        setFiMsg(`⚠ Cannot invoice ${qty} pcs. FI PASS: ${fiQty}, already invoiced: ${getInvoicedQty(sel.po)}.`);return;
      }
    }
    addItem('invoices',{...form,amount_usd:parseFloat(form.amount_usd||0)});
    setForm({});setFiMsg('');
  };

  const STABS=[['invoices','Invoices'],['lcs','LC Register'],['receipts','Payments Received'],['contracts','Sales Contracts']];
  return(
    <div>
      <div style={{display:'flex',gap:'6px',marginBottom:'14px'}}>
        {STABS.map(([id,l])=><button key={id} style={S.tab(sub===id)} onClick={()=>{setSub(id);setForm({});setFiMsg('');}}>{l}</button>)}
      </div>

      {sub==='invoices'&&(
        <>
          <div style={S.card()}>
            <div style={S.hdr}><span>CREATE INVOICE</span><span style={{fontSize:'0.68rem',color:'#676879',fontWeight:'normal'}}>Qty validated against Final Inspection PASS</span></div>
            <MsgBar msg={fiMsg} type={fiMsg.startsWith('✓')?'ok':'err'}/>
            <div style={S.grid('repeat(4,1fr)')}>
              <Fld label="Invoice No"><input style={S.inp} value={F('inv_no')} onChange={e=>sf('inv_no',e.target.value)} placeholder="INV-2026-001"/></Fld>
              <Fld label="PO Ref">
                <select style={S.sel} value={F('po_ref')} onChange={e=>{const p=poOpts.find(x=>x.po===e.target.value);sf('po_ref',e.target.value);if(p){sf('buyer',p.styleRef);sf('unit_price',p.fob);}setFiMsg('');}}>
                  <option value="">— Select PO —</option>
                  {poOpts.map(p=><option key={p.po} value={p.po}>{p.label}</option>)}
                </select>
              </Fld>
              <Fld label="Buyer / Style"><input style={S.inp} value={F('buyer')} onChange={e=>sf('buyer',e.target.value)}/></Fld>
              <Fld label=" "><button style={{...S.btn('#7c3aed'),width:'100%',marginTop:'20px'}} onClick={loadFromFI}>🔍 Load from Final Inspection</button></Fld>
              <Fld label="Invoice Date"><input type="date" style={S.inp} value={F('date')||today()} onChange={e=>sf('date',e.target.value)}/></Fld>
              <Fld label="Qty Shipped">
                <input type="number" style={{...S.inp,background:F('_fi_locked')?'#f0fdf4':undefined}} value={F('qty')}
                  onChange={e=>{sf('qty',e.target.value);sf('amount_usd',(parseFloat(F('unit_price'))||0)*parseFloat(e.target.value||0));sf('_fi_locked',false);}}/>
              </Fld>
              <Fld label="FOB / Unit Price"><input type="number" step="0.01" style={S.inp} value={F('unit_price')} onChange={e=>{sf('unit_price',e.target.value);sf('amount_usd',(parseFloat(F('qty'))||0)*parseFloat(e.target.value||0));}}/></Fld>
              <Fld label="Amount (USD)"><input type="number" style={S.inp} value={F('amount_usd')} onChange={e=>sf('amount_usd',e.target.value)}/></Fld>
              <Fld label="Due Date"><input type="date" style={S.inp} value={F('due_date')} onChange={e=>sf('due_date',e.target.value)}/></Fld>
              <Fld label="LC / Contract Ref"><input style={S.inp} value={F('lc_ref')} onChange={e=>sf('lc_ref',e.target.value)}/></Fld>
              <Fld label="Status"><select style={S.sel} value={F('status')||'Pending'} onChange={e=>sf('status',e.target.value)}>
                {['Pending','Sent','Partially Paid','Paid','Overdue'].map(s=><option key={s}>{s}</option>)}</select></Fld>
              <Fld label=" "><button style={{...S.btn('#00a65e'),marginTop:'20px',width:'100%'}} onClick={addInvoice}>+ Add Invoice</button></Fld>
            </div>
          </div>
          <div style={S.card()}>
            <div style={S.hdr}><span>INVOICES ({arr(fin.invoices).length})</span><strong style={{color:'#00a65e'}}>Total: {fmtC(arr(fin.invoices).reduce((s,i)=>s+(parseFloat(i.amount_usd)||0),0))}</strong></div>
            <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Invoice#','PO Ref','Buyer','Date','Qty','Amount','Due Date','LC Ref','Status','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
              <tbody>{arr(fin.invoices).map(inv=>{
                const od=inv.status!=='Paid'&&inv.due_date&&new Date(inv.due_date)<new Date();
                return(<tr key={inv.id} style={{background:od?'#fef2f2':'white'}}>
                  <td style={{...S.TD,fontWeight:'bold',color:'#2563eb'}}>{inv.inv_no}</td>
                  <td style={S.TD}>{inv.po_ref}</td><td style={S.TD}>{inv.buyer}</td><td style={S.TD}>{inv.date}</td>
                  <td style={{...S.TD,textAlign:'right'}}>{fmt(inv.qty)}</td>
                  <td style={{...S.TD,fontWeight:'bold',color:'#00a65e',textAlign:'right'}}>{fmtC(inv.amount_usd)}</td>
                  <td style={{...S.TD,color:od?'#e2445c':'inherit'}}>{inv.due_date}{od?' ⚠':''}</td>
                  <td style={S.TD}>{inv.lc_ref||'—'}</td>
                  <td style={S.TD}><span style={{padding:'2px 7px',borderRadius:'8px',fontSize:'0.68rem',fontWeight:'bold',background:inv.status==='Paid'?'#dcfce7':od?'#fee2e2':'#fef3c7',color:inv.status==='Paid'?'#166534':od?'#991b1b':'#92400e'}}>{inv.status}</span></td>
                  <td style={S.TD}><button onClick={()=>delItem('invoices',inv.id)} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer',fontWeight:'bold'}}>✕</button></td>
                </tr>);})}
            </tbody></table></div>
          </div>
        </>
      )}

      {sub==='lcs'&&(
        <>
          <div style={S.card()}>
            <div style={S.hdr}>ADD LETTER OF CREDIT</div>
            <div style={S.grid('repeat(4,1fr)')}>
              {[['lc_no','LC Number'],['bank','Issuing Bank'],['beneficiary','Beneficiary'],['date','Issue Date'],['expiry','Expiry Date'],['amount','Amount (USD)'],['currency','Currency'],['invoice_ref','Invoice Ref']].map(([k,l])=>(
                <Fld key={k} label={l}><input type={k.includes('date')||k.includes('expiry')?'date':k==='amount'?'number':'text'} style={S.inp} value={F(k)} onChange={e=>sf(k,e.target.value)}/></Fld>
              ))}
              <Fld label="Status"><select style={S.sel} value={F('status')||'Active'} onChange={e=>sf('status',e.target.value)}>
                {['Active','Documents Sent','Negotiated','Discrepant','Closed'].map(s=><option key={s}>{s}</option>)}</select></Fld>
              <Fld label="Notes" span={2}><input style={S.inp} value={F('notes')} onChange={e=>sf('notes',e.target.value)}/></Fld>
              <Fld label=" "><button style={{...S.btn('#0073ea'),marginTop:'20px',width:'100%'}} onClick={()=>{addItem('lcs',form);setForm({});}}>+ Add LC</button></Fld>
            </div>
          </div>
          <div style={S.card()}>
            <div style={S.hdr}>LC REGISTER ({arr(fin.lcs).length})</div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['LC No','Bank','Beneficiary','Issue Date','Expiry','Amount','Invoice Ref','Status','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
              <tbody>{arr(fin.lcs).map(lc=>{
                const exp=lc.expiry&&new Date(lc.expiry)<new Date();
                return(<tr key={lc.id}>
                  <td style={{...S.TD,fontWeight:'bold',color:'#2563eb'}}>{lc.lc_no}</td>
                  <td style={S.TD}>{lc.bank}</td><td style={S.TD}>{lc.beneficiary}</td><td style={S.TD}>{lc.date}</td>
                  <td style={{...S.TD,color:exp?'#e2445c':'inherit'}}>{lc.expiry}{exp?' ⚠':''}</td>
                  <td style={{...S.TD,fontWeight:'bold',color:'#00a65e',textAlign:'right'}}>{fmtC(lc.amount)}</td>
                  <td style={S.TD}>{lc.invoice_ref||'—'}</td>
                  <td style={S.TD}><span style={{padding:'2px 7px',borderRadius:'8px',fontSize:'0.68rem',fontWeight:'bold',background:lc.status==='Closed'?'#f0f1f4':lc.status==='Negotiated'?'#dcfce7':'#dbeafe',color:lc.status==='Closed'?'#676879':lc.status==='Negotiated'?'#166534':'#1e40af'}}>{lc.status}</span></td>
                  <td style={S.TD}><button onClick={()=>delItem('lcs',lc.id)} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
                </tr>);})}
            </tbody></table>
          </div>
        </>
      )}

      {sub==='receipts'&&(
        <>
          <div style={S.card()}>
            <div style={S.hdr}>RECORD PAYMENT RECEIVED</div>
            <div style={S.grid('repeat(4,1fr)')}>
              <Fld label="Date"><input type="date" style={S.inp} value={F('date')||today()} onChange={e=>sf('date',e.target.value)}/></Fld>
              <Fld label="Invoice Ref"><select style={S.sel} value={F('invoice_ref')} onChange={e=>sf('invoice_ref',e.target.value)}>
                <option value="">— Select Invoice —</option>
                {arr(fin.invoices).map(i=><option key={i.id} value={i.inv_no}>{i.inv_no} · {fmtC(i.amount_usd)} · {i.buyer}</option>)}
              </select></Fld>
              <Fld label="Amount (USD)"><input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)}/></Fld>
              <Fld label="Method"><select style={S.sel} value={F('method')||'Bank Transfer'} onChange={e=>sf('method',e.target.value)}>
                {['Bank Transfer','LC Payment','Cash','Cheque','SWIFT','Others'].map(m=><option key={m}>{m}</option>)}</select></Fld>
              <Fld label="Bank Reference"><input style={S.inp} value={F('reference')} onChange={e=>sf('reference',e.target.value)} placeholder="TXN/UTR No."/></Fld>
              <Fld label="Exchange Rate"><input type="number" step="0.01" style={S.inp} value={F('exchange_rate')||1} onChange={e=>sf('exchange_rate',e.target.value)}/></Fld>
              <Fld label="Notes"><input style={S.inp} value={F('notes')} onChange={e=>sf('notes',e.target.value)}/></Fld>
              <Fld label=" "><button style={{...S.btn('#00a65e'),marginTop:'20px',width:'100%'}} onClick={()=>{addItem('receipts',{...form,amount:parseFloat(form.amount||0)});setForm({});}}>+ Record Receipt</button></Fld>
            </div>
          </div>
          <div style={S.card()}>
            <div style={S.hdr}><span>RECEIPTS ({arr(fin.receipts).length})</span><strong style={{color:'#00a65e'}}>Total: {fmtC(arr(fin.receipts).reduce((s,r)=>s+(parseFloat(r.amount)||0),0))}</strong></div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Date','Invoice','Amount','Method','Bank Ref','Notes','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
              <tbody>{arr(fin.receipts).map((r,i)=>(
                <tr key={r.id||i}><td style={S.TD}>{r.date}</td><td style={S.TD}>{r.invoice_ref}</td>
                  <td style={{...S.TD,fontWeight:'bold',color:'#00a65e',textAlign:'right'}}>{fmtC(r.amount)}</td>
                  <td style={S.TD}>{r.method}</td><td style={S.TD}>{r.reference}</td><td style={S.TD}>{r.notes}</td>
                  <td style={S.TD}><button onClick={()=>delItem('receipts',r.id)} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
                </tr>))}
            </tbody></table>
          </div>
        </>
      )}

      {sub==='contracts'&&(
        <>
          <div style={S.card()}>
            <div style={S.hdr}>ADD SALES CONTRACT</div>
            <div style={S.grid('repeat(4,1fr)')}>
              {[['contract_no','Contract No'],['buyer','Buyer'],['date','Date'],['delivery_date','Delivery Date'],['amount','Value (USD)'],['payment_terms','Payment Terms'],['port_of_loading','Port of Loading'],['port_of_discharge','Port of Discharge']].map(([k,l])=>(
                <Fld key={k} label={l}><input type={k.includes('date')?'date':k==='amount'?'number':'text'} style={S.inp} value={F(k)} onChange={e=>sf(k,e.target.value)}/></Fld>
              ))}
              <Fld label=" "><button style={{...S.btn('#0073ea'),marginTop:'20px',width:'100%'}} onClick={()=>{addItem('salesContracts',form);setForm({});}}>+ Add Contract</button></Fld>
            </div>
          </div>
          <div style={S.card()}>
            <div style={S.hdr}>SALES CONTRACTS ({arr(fin.salesContracts).length})</div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Contract No','Buyer','Date','Delivery','Value','Payment Terms','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
              <tbody>{arr(fin.salesContracts).map((c,i)=>(
                <tr key={c.id||i}>
                  <td style={{...S.TD,fontWeight:'bold',color:'#2563eb'}}>{c.contract_no}</td>
                  <td style={S.TD}>{c.buyer}</td><td style={S.TD}>{c.date}</td><td style={S.TD}>{c.delivery_date}</td>
                  <td style={{...S.TD,fontWeight:'bold',color:'#00a65e',textAlign:'right'}}>{fmtC(c.amount)}</td>
                  <td style={S.TD}>{c.payment_terms}</td>
                  <td style={S.TD}><button onClick={()=>delItem('salesContracts',c.id)} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
                </tr>))}
            </tbody></table>
          </div>
        </>
      )}
    </div>
  );
}

// ── AI DOCUMENT SCANNER ───────────────────────────────────────────────────────
function AIDocScanner({fin,upd,db,flash}){
  const [docType,setDocType]=useState('PO');
  const [file,setFile]=useState(null);
  const [preview,setPreview]=useState('');
  const [extracting,setExtracting]=useState(false);
  const [extracted,setExtracted]=useState(null);
  const [msg,setMsg]=useState('');
  const fileRef=useRef();

  const DOC={
    PO:{label:'Purchase Order',fields:['po_number','buyer','style_ref','order_qty','fob_price','ex_factory_date','destination','payment_terms','currency']},
    LC:{label:'Letter of Credit',fields:['lc_no','bank','beneficiary','amount','currency','issue_date','expiry_date','invoice_ref','terms']},
    RECEIPT:{label:'Receipt / Invoice',fields:['invoice_no','buyer','date','amount_usd','currency','po_ref','description','payment_method','bank_reference']},
    EXPENSE:{label:'Expense Receipt',fields:['date','vendor','description','amount','category','payment_method','reference']},
  };

  const handleFile=async(f)=>{if(!f)return;setFile(f);setExtracted(null);setMsg('');if(f.type.startsWith('image/')){const r=new FileReader();r.onload=e=>setPreview(e.target.result);r.readAsDataURL(f);}else setPreview('');};

  const extract=async()=>{
    if(!file){setMsg('Upload a document first.');return;}
    setExtracting(true);setExtracted(null);setMsg('');
    try{
      const fields=DOC[docType].fields;
      let content=[];
      if(file.type.startsWith('image/')||file.type==='application/pdf'){
        const b64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(',')[1]);r.onerror=rej;r.readAsDataURL(file);});
        if(file.type.startsWith('image/'))
          content=[{type:'image',source:{type:'base64',media_type:file.type,data:b64}},{type:'text',text:`Extract from this ${DOC[docType].label}: ${fields.join(', ')}. Return ONLY valid JSON object, no other text.`}];
        else
          content=[{type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}},{type:'text',text:`Extract from this ${DOC[docType].label}: ${fields.join(', ')}. Return ONLY valid JSON object.`}];
      }else{
        const txt=await file.text();
        content=[{type:'text',text:`Document:\n\n${txt.slice(0,4000)}\n\nExtract: ${fields.join(', ')}. Return ONLY valid JSON object.`}];
      }
      const resp=await fetch('https://api.anthropic.com/v1/messages',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({model:'claude-sonnet-4-6',max_tokens:1000,messages:[{role:'user',content}]})});
      const data=await resp.json();
      const parsed=JSON.parse((data.content?.[0]?.text||'{}').replace(/```json?|```/g,'').trim());
      setExtracted(parsed);
      setMsg('✓ Extraction complete. Review and confirm to save.');
    }catch(e){setMsg('Extraction failed: '+e.message);}
    finally{setExtracting(false);}
  };

  const confirm=()=>{
    if(!extracted)return;
    if(docType==='LC') upd('lcs')([...arr(fin.lcs),{id:Date.now(),created:today(),source:'AI_SCAN',...extracted}]);
    else if(docType==='RECEIPT') upd('receipts')([...arr(fin.receipts),{id:Date.now(),created:today(),source:'AI_SCAN',amount:parseFloat(extracted.amount_usd||0),...extracted}]);
    else if(docType==='EXPENSE') upd('expenses')([...arr(fin.expenses),{id:Date.now(),created:today(),source:'AI_SCAN',amount:parseFloat(extracted.amount||0),...extracted}]);
    else upd('salesContracts')([...arr(fin.salesContracts),{id:Date.now(),created:today(),source:'AI_SCAN',contract_no:extracted.po_number,buyer:extracted.buyer,amount:parseFloat(extracted.fob_price||0),delivery_date:extracted.ex_factory_date,...extracted}]);
    flash(`${DOC[docType].label} saved to database from scan ✓`);
    setExtracted(null);setFile(null);setPreview('');setMsg('');
  };

  return(
    <div>
      <div style={S.card()}>
        <div style={S.hdr}><span>🤖 AI DOCUMENT SCANNER</span><span style={{fontSize:'0.68rem',color:'#676879',fontWeight:'normal'}}>Upload scan → AI extracts fields → saves to DB</span></div>
        <MsgBar msg={msg} type={msg.startsWith('✓')?'ok':'err'}/>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Document Type" span={2}>
            <select style={S.sel} value={docType} onChange={e=>{setDocType(e.target.value);setExtracted(null);setMsg('');}}>
              {Object.entries(DOC).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </Fld>
        </div>
        <div style={{border:'2px dashed #c5c7d4',borderRadius:'8px',padding:'28px',textAlign:'center',marginTop:'12px',background:'#f8f9fb',cursor:'pointer'}}
          onClick={()=>fileRef.current.click()} onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}}>
          <input ref={fileRef} type="file" accept="image/*,.pdf,.txt" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
          {preview?<img src={preview} alt="preview" style={{maxHeight:'180px',maxWidth:'100%',borderRadius:'6px'}}/>:
            <div><div style={{fontSize:'2rem',marginBottom:'6px'}}>📄</div>
              <div style={{color:'#676879',fontSize:'0.85rem'}}>Drop or click to upload</div>
              <div style={{color:'#c5c7d4',fontSize:'0.7rem',marginTop:'3px'}}>JPG · PNG · PDF · TXT</div>
              {file&&<div style={{color:'#0073ea',marginTop:'6px',fontSize:'0.8rem',fontWeight:'bold'}}>📎 {file.name}</div>}
            </div>}
        </div>
        <div style={{display:'flex',gap:'10px',marginTop:'12px'}}>
          <button style={S.btn('#7c3aed')} onClick={extract} disabled={extracting||!file}>{extracting?'⟳ Extracting…':'🤖 Extract with AI'}</button>
          <button style={S.btn('#f0f1f4','#323338')} onClick={()=>{setFile(null);setPreview('');setExtracted(null);setMsg('');}}>Clear</button>
        </div>
      </div>
      {extracted&&(
        <div style={S.card('#00a65e')}>
          <div style={S.hdr}><span>✓ REVIEW EXTRACTED DATA — {DOC[docType].label}</span><button style={S.btn('#00a65e')} onClick={confirm}>✓ Confirm & Save to DB</button></div>
          <div style={S.grid('repeat(3,1fr)')}>
            {Object.entries(extracted).map(([k,v])=>(
              <Fld key={k} label={k.replace(/_/g,' ')}><input style={S.inp} value={v||''} onChange={e=>setExtracted(p=>({...p,[k]:e.target.value}))}/></Fld>
            ))}
          </div>
        </div>
      )}
      <div style={S.card()}>
        <div style={S.hdr}>HOW IT WORKS</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
          {[['1️⃣','Choose Type','Select PO, LC, Receipt or Expense'],['2️⃣','Upload','Photo, PDF or text scan'],['3️⃣','AI Extracts','Claude reads all key fields'],['4️⃣','Confirm','Review → saved to database']].map(([ic,t,d])=>(
            <div key={t} style={{textAlign:'center',padding:'14px',background:'#f8f9fb',borderRadius:'8px'}}>
              <div style={{fontSize:'1.4rem',marginBottom:'5px'}}>{ic}</div>
              <div style={{fontWeight:'bold',fontSize:'0.78rem',marginBottom:'3px'}}>{t}</div>
              <div style={{color:'#676879',fontSize:'0.7rem'}}>{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── EXPENSES ──────────────────────────────────────────────────────────────────
function Expenses({fin,upd}){
  const [form,setForm]=useState({date:today(),category:'Miscellaneous',payment_method:'Bank'});
  const F=(k)=>form[k]||'';const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const bycat=useMemo(()=>{const m={};arr(fin.expenses).forEach(e=>{m[e.category]=(m[e.category]||0)+parseFloat(e.amount||0);});return Object.entries(m).sort((a,b)=>b[1]-a[1]);},[fin.expenses]);
  return(
    <div>
      <div style={S.card()}>
        <div style={S.hdr}>PUNCH EXPENSE</div>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Date"><input type="date" style={S.inp} value={F('date')} onChange={e=>sf('date',e.target.value)}/></Fld>
          <Fld label="Category"><select style={S.sel} value={F('category')} onChange={e=>sf('category',e.target.value)}>{EXPENSE_CATS.map(c=><option key={c}>{c}</option>)}</select></Fld>
          <Fld label="Description"><input style={S.inp} value={F('description')} onChange={e=>sf('description',e.target.value)}/></Fld>
          <Fld label="Amount (USD)"><input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)}/></Fld>
          <Fld label="Vendor"><input style={S.inp} value={F('vendor')} onChange={e=>sf('vendor',e.target.value)}/></Fld>
          <Fld label="Payment Method"><select style={S.sel} value={F('payment_method')} onChange={e=>sf('payment_method',e.target.value)}>{['Bank','Cash','Cheque','UPI','Credit Card'].map(m=><option key={m}>{m}</option>)}</select></Fld>
          <Fld label="Ref / Bill No"><input style={S.inp} value={F('ref')} onChange={e=>sf('ref',e.target.value)}/></Fld>
          <Fld label=" "><button style={{...S.btn('#e2445c'),marginTop:'20px',width:'100%'}} onClick={()=>{upd('expenses')([...arr(fin.expenses),{id:Date.now(),source:'MANUAL',...form,amount:parseFloat(form.amount||0)}]);setForm({date:today(),category:'Miscellaneous',payment_method:'Bank'});}}>+ Add Expense</button></Fld>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'14px'}}>
        <div style={S.card()}>
          <div style={S.hdr}>BY CATEGORY</div>
          {bycat.map(([c,v])=>(
            <div key={c} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #f8fafc',fontSize:'0.75rem'}}>
              <span>{c}</span><strong style={{color:'#e2445c'}}>{fmtC(v)}</strong>
            </div>
          ))}
          <div style={{marginTop:'8px',padding:'6px 8px',background:'#fef2f2',borderRadius:'6px',display:'flex',justifyContent:'space-between',fontWeight:'bold',fontSize:'0.82rem'}}>
            <span>TOTAL</span><span style={{color:'#e2445c'}}>{fmtC(arr(fin.expenses).reduce((s,e)=>s+(parseFloat(e.amount)||0),0))}</span>
          </div>
        </div>
        <div style={S.card()}>
          <div style={S.hdr}>ALL EXPENSES ({arr(fin.expenses).length})</div>
          <div style={{overflowX:'auto',maxHeight:'400px',overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead style={{position:'sticky',top:0}}><tr>{['Date','Category','Description','Vendor','Amount','Method','Source','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
              <tbody>{arr(fin.expenses).map((e,i)=>(
                <tr key={e.id||i}>
                  <td style={S.TD}>{e.date}</td>
                  <td style={S.TD}><span style={{fontSize:'0.68rem',padding:'2px 6px',borderRadius:'8px',background:'#f0f1f4',color:'#676879'}}>{e.category}</span></td>
                  <td style={S.TD}>{e.description}</td><td style={S.TD}>{e.vendor}</td>
                  <td style={{...S.TD,fontWeight:'bold',color:'#e2445c',textAlign:'right'}}>{fmtC(e.amount)}</td>
                  <td style={S.TD}>{e.payment_method}</td>
                  <td style={S.TD}><span style={{fontSize:'0.62rem',color:['AI','AI_SCAN','AI_BANK'].includes(e.source)?'#7c3aed':e.source==='CSV_IMPORT'?'#0073ea':'#676879'}}>{e.source||'MANUAL'}</span></td>
                  <td style={S.TD}><button onClick={()=>upd('expenses')(arr(fin.expenses).filter(x=>x.id!==e.id))} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PAYROLL — with CSV import ─────────────────────────────────────────────────
function Payroll({fin,upd}){
  const [form,setForm]=useState({month:today().slice(0,7),department:'Production'});
  const [csvMsg,setCsvMsg]=useState('');
  const csvRef=useRef();
  const F=(k)=>form[k]||'';
  const sf=(k,v)=>{const u={...form,[k]:v};u.net=(parseFloat(u.basic||0)+parseFloat(u.hra||0)+parseFloat(u.allowances||0))-parseFloat(u.deductions||0)-parseFloat(u.pf||0)-parseFloat(u.tax_deduction||0);setForm(u);};
  const months=useMemo(()=>[...new Set(arr(fin.salaries).map(s=>s.month))].sort().reverse(),[fin.salaries]);
  const [selM,setSelM]=useState('');
  const filtered=selM?arr(fin.salaries).filter(s=>s.month===selM):fin.salaries;

  const dlTemplate=()=>{
    const hdr='employee_id,name,designation,department,month,basic,hra,allowances,pf,tax_deduction,deductions';
    const sample='E001,John Smith,Operator,Production,'+today().slice(0,7)+',500,50,30,20,10,0';
    const a=document.createElement('a');a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(hdr+'\n'+sample);a.download='employee_salary_template.csv';a.click();
  };

  const importCSV=(file)=>{
    if(!file)return;
    const r=new FileReader();
    r.onload=e=>{
      try{
        const lines=e.target.result.trim().split('\n');
        const hdrs=lines[0].split(',').map(h=>h.trim().toLowerCase());
        const rows=lines.slice(1).map(line=>{
          const vals=line.split(',').map(v=>v.trim());
          const obj={};hdrs.forEach((h,i)=>{obj[h]=vals[i]||'';});
          obj.net=(parseFloat(obj.basic||0)+parseFloat(obj.hra||0)+parseFloat(obj.allowances||0))-parseFloat(obj.deductions||0)-parseFloat(obj.pf||0)-parseFloat(obj.tax_deduction||0);
          obj.id=Date.now()+Math.random();obj.source='CSV_IMPORT';
          return obj;
        }).filter(r=>r.name);
        upd('salaries')([...arr(fin.salaries),...rows]);
        setCsvMsg(`✓ ${rows.length} records imported and saved to database.`);
      }catch(ex){setCsvMsg('CSV error: '+ex.message);}
    };
    r.readAsText(file);
  };

  return(
    <div>
      <div style={S.card()}>
        <div style={S.hdr}><span>CSV EMPLOYEE IMPORT</span>
          <div style={{display:'flex',gap:'8px'}}>
            <button style={S.btn('#676879')} onClick={dlTemplate}>⬇ Template</button>
            <button style={S.btn('#0073ea')} onClick={()=>csvRef.current.click()}>📂 Upload CSV</button>
            <input ref={csvRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>importCSV(e.target.files[0])}/>
          </div>
        </div>
        <MsgBar msg={csvMsg} type={csvMsg.startsWith('✓')?'ok':'err'}/>
        <div style={{color:'#676879',fontSize:'0.78rem'}}>Download template → fill employee data → upload CSV → all records save to database automatically.<br/>
        Required columns: <code>employee_id, name, designation, department, month, basic, hra, allowances, pf, tax_deduction, deductions</code></div>
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>ADD SALARY RECORD (MANUAL)</div>
        <div style={S.grid('repeat(5,1fr)')}>
          <Fld label="Month"><input type="month" style={S.inp} value={F('month')} onChange={e=>sf('month',e.target.value)}/></Fld>
          <Fld label="Employee Name"><input style={S.inp} value={F('name')} onChange={e=>sf('name',e.target.value)}/></Fld>
          <Fld label="Designation"><input style={S.inp} value={F('designation')} onChange={e=>sf('designation',e.target.value)}/></Fld>
          <Fld label="Department"><select style={S.sel} value={F('department')} onChange={e=>sf('department',e.target.value)}>{['Production','Admin','Quality','Merchandising','Finance','IT','HR'].map(d=><option key={d}>{d}</option>)}</select></Fld>
          <Fld label="Basic"><input type="number" style={S.inp} value={F('basic')} onChange={e=>sf('basic',e.target.value)}/></Fld>
          <Fld label="HRA"><input type="number" style={S.inp} value={F('hra')} onChange={e=>sf('hra',e.target.value)}/></Fld>
          <Fld label="Allowances"><input type="number" style={S.inp} value={F('allowances')} onChange={e=>sf('allowances',e.target.value)}/></Fld>
          <Fld label="PF"><input type="number" style={S.inp} value={F('pf')} onChange={e=>sf('pf',e.target.value)}/></Fld>
          <Fld label="TDS"><input type="number" style={S.inp} value={F('tax_deduction')} onChange={e=>sf('tax_deduction',e.target.value)}/></Fld>
          <Fld label="Other Deductions"><input type="number" style={S.inp} value={F('deductions')} onChange={e=>sf('deductions',e.target.value)}/></Fld>
          <Fld label="Net Pay"><input readOnly style={{...S.inp,background:'#f0fdf4',fontWeight:'bold',color:'#00a65e'}} value={fmt(F('net'),2)}/></Fld>
          <Fld label=" " span={2}><button style={{...S.btn('#00a65e'),marginTop:'20px',width:'100%'}} onClick={()=>{upd('salaries')([...arr(fin.salaries),{id:Date.now(),...form,net:parseFloat(form.net||0)}]);setForm({month:today().slice(0,7),department:'Production'});}}>+ Add Salary</button></Fld>
        </div>
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>
          <span>PAYROLL REGISTER</span>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            <select style={{...S.sel,width:'150px'}} value={selM} onChange={e=>setSelM(e.target.value)}>
              <option value="">All Months</option>{months.map(m=><option key={m}>{m}</option>)}
            </select>
            <strong style={{color:'#00a65e'}}>{fmtC(filtered.reduce((s,r)=>s+parseFloat(r.net||0),0))}</strong>
          </div>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Month','ID','Name','Designation','Dept','Basic','HRA','Allow','PF','TDS','Net Pay','Source','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{filtered.map(r=>(
              <tr key={r.id}>
                <td style={S.TD}>{r.month}</td><td style={{...S.TD,color:'#676879',fontSize:'0.72rem'}}>{r.employee_id||'—'}</td>
                <td style={{...S.TD,fontWeight:'bold'}}>{r.name}</td><td style={S.TD}>{r.designation}</td><td style={S.TD}>{r.department}</td>
                <td style={{...S.TD,textAlign:'right'}}>{fmt(r.basic)}</td><td style={{...S.TD,textAlign:'right'}}>{fmt(r.hra)}</td>
                <td style={{...S.TD,textAlign:'right'}}>{fmt(r.allowances)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#e2445c'}}>{fmt(r.pf)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#e2445c'}}>{fmt(r.tax_deduction)}</td>
                <td style={{...S.TD,fontWeight:'bold',color:'#00a65e',textAlign:'right'}}>{fmtC(r.net)}</td>
                <td style={S.TD}><span style={{fontSize:'0.62rem',color:r.source==='CSV_IMPORT'?'#0073ea':'#676879'}}>{r.source||'MANUAL'}</span></td>
                <td style={S.TD}><button onClick={()=>upd('salaries')(arr(fin.salaries).filter(x=>x.id!==r.id))} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── ASSETS ────────────────────────────────────────────────────────────────────
function Assets({fin,upd}){
  const [form,setForm]=useState({dep_method:'SLM',useful_life:5,purchase_date:today()});
  const F=(k)=>form[k]||'';const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const totDep=arr(fin.assets).reduce((s,a)=>s+calcDep(a).annual,0);
  const totGross=arr(fin.assets).reduce((s,a)=>s+parseFloat(a.cost||0),0);
  const totNet=arr(fin.assets).reduce((s,a)=>s+calcDep(a).bookValue,0);
  return(
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'14px',marginBottom:'16px'}}>
        {[['Gross Block',totGross,'#0073ea'],['Net Block',totNet,'#00a65e'],['Annual Dep',totDep,'#f59e0b']].map(([l,v,c])=>(
          <div key={l} style={S.kpi(c)}><div style={{fontSize:'0.68rem',fontWeight:'bold',color:'#676879'}}>{l}</div>
            <div style={{fontSize:'1.2rem',fontWeight:'900',color:c}}>{fmtC(v)}</div></div>
        ))}
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>ADD ASSET</div>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Asset Name"><input style={S.inp} value={F('name')} onChange={e=>sf('name',e.target.value)}/></Fld>
          <Fld label="Category"><select style={S.sel} value={F('category')||'Plant & Machinery'} onChange={e=>sf('category',e.target.value)}>{['Plant & Machinery','Furniture','Computer & IT','Vehicle','Building','Land','Other'].map(c=><option key={c}>{c}</option>)}</select></Fld>
          <Fld label="Purchase Date"><input type="date" style={S.inp} value={F('purchase_date')} onChange={e=>sf('purchase_date',e.target.value)}/></Fld>
          <Fld label="Cost (USD)"><input type="number" style={S.inp} value={F('cost')} onChange={e=>sf('cost',e.target.value)}/></Fld>
          <Fld label="Salvage Value"><input type="number" style={S.inp} value={F('salvage')||0} onChange={e=>sf('salvage',e.target.value)}/></Fld>
          <Fld label="Useful Life (yrs)"><input type="number" style={S.inp} value={F('useful_life')} onChange={e=>sf('useful_life',e.target.value)}/></Fld>
          <Fld label="Dep Method"><select style={S.sel} value={F('dep_method')} onChange={e=>sf('dep_method',e.target.value)}><option value="SLM">Straight Line</option><option value="WDV">Written Down Value</option></select></Fld>
          <Fld label=" "><button style={{...S.btn('#0073ea'),marginTop:'20px',width:'100%'}} onClick={()=>{upd('assets')([...arr(fin.assets),{id:Date.now(),...form}]);setForm({dep_method:'SLM',useful_life:5,purchase_date:today()});}}>+ Add Asset</button></Fld>
        </div>
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>ASSET REGISTER ({arr(fin.assets).length})</div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>{['Name','Category','Purchase Date','Cost','Dep Method','Annual Dep','Accumulated','Book Value','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
          <tbody>{arr(fin.assets).map(a=>{const d=calcDep(a);return(
            <tr key={a.id}>
              <td style={{...S.TD,fontWeight:'bold'}}>{a.name}</td><td style={S.TD}>{a.category}</td><td style={S.TD}>{a.purchase_date}</td>
              <td style={{...S.TD,textAlign:'right'}}>{fmtC(a.cost)}</td><td style={S.TD}>{a.dep_method}</td>
              <td style={{...S.TD,textAlign:'right',color:'#f59e0b',fontWeight:'bold'}}>{fmtC(d.annual)}</td>
              <td style={{...S.TD,textAlign:'right',color:'#e2445c'}}>{fmtC(d.accumulated)}</td>
              <td style={{...S.TD,textAlign:'right',color:'#00a65e',fontWeight:'bold'}}>{fmtC(d.bookValue)}</td>
              <td style={S.TD}><button onClick={()=>upd('assets')(arr(fin.assets).filter(x=>x.id!==a.id))} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
            </tr>);})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── LOANS ─────────────────────────────────────────────────────────────────────
function Loans({fin,upd}){
  const [form,setForm]=useState({type:'Term Loan',date:today()});
  const F=(k)=>form[k]||'';const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const totEMI=arr(fin.loans).reduce((s,l)=>s+calcEMI(parseFloat(l.amount||0),parseFloat(l.rate||0),parseInt(l.tenure||60)),0);
  const totOut=arr(fin.loans).reduce((s,l)=>s+parseFloat(l.amount||0)*(1-parseInt(l.paid_emis||0)/Math.max(parseInt(l.tenure||60),1)),0);
  return(
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'14px',marginBottom:'16px'}}>
        {[['Total Loans',arr(fin.loans).reduce((s,l)=>s+parseFloat(l.amount||0),0),'#0073ea'],['Outstanding',totOut,'#e2445c'],['Monthly EMI',totEMI,'#f59e0b']].map(([l,v,c])=>(
          <div key={l} style={S.kpi(c)}><div style={{fontSize:'0.68rem',fontWeight:'bold',color:'#676879'}}>{l}</div><div style={{fontSize:'1.2rem',fontWeight:'900',color:c}}>{fmtC(v)}</div></div>
        ))}
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>ADD LOAN</div>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Loan Type"><select style={S.sel} value={F('type')} onChange={e=>sf('type',e.target.value)}>{['Term Loan','Working Capital','Overdraft','Equipment Finance','Export Credit','Personal'].map(t=><option key={t}>{t}</option>)}</select></Fld>
          <Fld label="Lender"><input style={S.inp} value={F('lender')} onChange={e=>sf('lender',e.target.value)}/></Fld>
          <Fld label="Principal (USD)"><input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)}/></Fld>
          <Fld label="Rate (% p.a.)"><input type="number" step="0.01" style={S.inp} value={F('rate')} onChange={e=>sf('rate',e.target.value)}/></Fld>
          <Fld label="Tenure (months)"><input type="number" style={S.inp} value={F('tenure')} onChange={e=>sf('tenure',e.target.value)}/></Fld>
          <Fld label="Start Date"><input type="date" style={S.inp} value={F('date')} onChange={e=>sf('date',e.target.value)}/></Fld>
          <Fld label="EMIs Paid"><input type="number" style={S.inp} value={F('paid_emis')||0} onChange={e=>sf('paid_emis',e.target.value)}/></Fld>
          <Fld label=" "><button style={{...S.btn('#0073ea'),marginTop:'20px',width:'100%'}} onClick={()=>{upd('loans')([...arr(fin.loans),{id:Date.now(),...form}]);setForm({type:'Term Loan',date:today()});}}>+ Add Loan</button></Fld>
        </div>
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>LOAN REGISTER ({arr(fin.loans).length})</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Type','Lender','Principal','Rate%','Tenure','EMI/m','Paid','Outstanding','Int Total','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{arr(fin.loans).map(l=>{
              const emi=calcEMI(parseFloat(l.amount||0),parseFloat(l.rate||0),parseInt(l.tenure||60));
              const paid=parseInt(l.paid_emis||0),ten=Math.max(parseInt(l.tenure||60),1);
              const out=parseFloat(l.amount||0)*(1-paid/ten);
              const intTot=(emi*ten)-parseFloat(l.amount||0);
              return(<tr key={l.id}>
                <td style={S.TD}>{l.type}</td><td style={{...S.TD,fontWeight:'bold'}}>{l.lender}</td>
                <td style={{...S.TD,textAlign:'right'}}>{fmtC(l.amount)}</td>
                <td style={{...S.TD,textAlign:'right'}}>{l.rate}%</td>
                <td style={{...S.TD,textAlign:'right'}}>{l.tenure}m</td>
                <td style={{...S.TD,fontWeight:'bold',color:'#0073ea',textAlign:'right'}}>{fmtC(emi)}</td>
                <td style={{...S.TD,textAlign:'right'}}>{paid}/{l.tenure}</td>
                <td style={{...S.TD,fontWeight:'bold',color:'#e2445c',textAlign:'right'}}>{fmtC(out)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#f59e0b'}}>{fmtC(intTot)}</td>
                <td style={S.TD}><button onClick={()=>upd('loans')(arr(fin.loans).filter(x=>x.id!==l.id))} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
              </tr>);})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── TAXATION ──────────────────────────────────────────────────────────────────
function Taxation({fin,upd,pl}){
  const [form,setForm]=useState({date:today(),type:'GST',period:today().slice(0,7)});
  const F=(k)=>form[k]||'';const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const taxDue=arr(fin.taxEntries).filter(t=>!t.paid&&t.due_date&&new Date(t.due_date)<new Date());
  return(
    <div>
      {taxDue.length>0&&<div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'8px',padding:'10px 14px',marginBottom:'14px',color:'#991b1b',fontWeight:'bold',fontSize:'0.8rem'}}>⚠ {taxDue.length} overdue: {taxDue.map(t=>`${t.type} ${t.period}`).join(', ')}</div>}
      <div style={S.card()}>
        <div style={S.hdr}>ADD TAX ENTRY</div>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Tax Type"><select style={S.sel} value={F('type')} onChange={e=>sf('type',e.target.value)}>{['GST','IGST','TDS','TCS','Corporate Tax','Export Duty','Others'].map(t=><option key={t}>{t}</option>)}</select></Fld>
          <Fld label="Period"><input type="month" style={S.inp} value={F('period')} onChange={e=>sf('period',e.target.value)}/></Fld>
          <Fld label="Amount (USD)"><input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)}/></Fld>
          <Fld label="Due Date"><input type="date" style={S.inp} value={F('due_date')} onChange={e=>sf('due_date',e.target.value)}/></Fld>
          <Fld label="Payment Date"><input type="date" style={S.inp} value={F('date')} onChange={e=>sf('date',e.target.value)}/></Fld>
          <Fld label="Reference"><input style={S.inp} value={F('reference')} onChange={e=>sf('reference',e.target.value)}/></Fld>
          <Fld label="Status"><select style={S.sel} value={F('paid')||'false'} onChange={e=>sf('paid',e.target.value==='true')}><option value="false">Pending</option><option value="true">Paid</option></select></Fld>
          <Fld label=" "><button style={{...S.btn('#7c3aed'),marginTop:'20px',width:'100%'}} onClick={()=>{upd('taxEntries')([...arr(fin.taxEntries),{id:Date.now(),...form,amount:parseFloat(form.amount||0)}]);setForm({date:today(),type:'GST',period:today().slice(0,7)});}}>+ Add Tax Entry</button></Fld>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'14px'}}>
        <div style={S.card()}>
          <div style={S.hdr}>SUMMARY</div>
          <div style={S.kpi('#e2445c')}><div style={{fontSize:'0.68rem',color:'#676879'}}>Total Tax Paid</div>
            <div style={{fontSize:'1.2rem',fontWeight:'900',color:'#e2445c'}}>{fmtC(arr(fin.taxEntries).reduce((s,t)=>s+parseFloat(t.amount||0),0))}</div></div>
          <div style={{marginTop:'10px'}}>{[...new Set(arr(fin.taxEntries).map(t=>t.type))].map(type=>{
            const tot=arr(fin.taxEntries).filter(t=>t.type===type).reduce((s,t)=>s+parseFloat(t.amount||0),0);
            return(<div key={type} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #f8fafc',fontSize:'0.75rem'}}>
              <span>{type}</span><strong style={{color:'#7c3aed'}}>{fmtC(tot)}</strong></div>);
          })}</div>
        </div>
        <div style={S.card()}>
          <div style={S.hdr}>TAX REGISTER</div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Type','Period','Amount','Due Date','Paid Date','Ref','Status','Del'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{arr(fin.taxEntries).map(t=>{
              const od=!t.paid&&t.due_date&&new Date(t.due_date)<new Date();
              return(<tr key={t.id} style={{background:od?'#fef2f2':'white'}}>
                <td style={{...S.TD,fontWeight:'bold'}}>{t.type}</td><td style={S.TD}>{t.period}</td>
                <td style={{...S.TD,fontWeight:'bold',color:'#7c3aed',textAlign:'right'}}>{fmtC(t.amount)}</td>
                <td style={{...S.TD,color:od?'#e2445c':'inherit'}}>{t.due_date}</td><td style={S.TD}>{t.date}</td><td style={S.TD}>{t.reference}</td>
                <td style={S.TD}><span style={{padding:'2px 7px',borderRadius:'8px',fontSize:'0.68rem',fontWeight:'bold',background:t.paid?'#dcfce7':od?'#fee2e2':'#fef3c7',color:t.paid?'#166534':od?'#991b1b':'#92400e'}}>{t.paid?'Paid':od?'OVERDUE':'Pending'}</span></td>
                <td style={S.TD}><button onClick={()=>upd('taxEntries')(arr(fin.taxEntries).filter(x=>x.id!==t.id))} style={{color:'#e2445c',background:'none',border:'none',cursor:'pointer'}}>✕</button></td>
              </tr>);})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── BANKING / AI BANK STATEMENT ───────────────────────────────────────────────
function Banking({fin,upd}){
  const [bankText,setBankText]=useState('');const [extracting,setExtracting]=useState(false);
  const [extracted,setExtracted]=useState([]);const [msg,setMsg]=useState('');const [bankName,setBankName]=useState('');
  const extract=async()=>{
    if(!bankText.trim()){setMsg('Paste bank statement first.');return;}
    setExtracting(true);setExtracted([]);setMsg('');
    try{
      const resp=await fetch('https://api.anthropic.com/v1/messages',{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({model:'claude-sonnet-4-6',max_tokens:1000,messages:[{role:'user',content:`Extract all transactions from this bank statement. Return ONLY a JSON array with: {date, description, amount (positive), category (one of: ${EXPENSE_CATS.join(',')}), type ("debit" or "credit")}.\n\n${bankText.slice(0,3000)}`}]})});
      const data=await resp.json();
      const rows=JSON.parse((data.content?.[0]?.text||'[]').replace(/```json?|```/g,'').trim());
      setExtracted(rows);setMsg(`✓ ${rows.length} transactions extracted.`);
    }catch(e){setMsg('Failed: '+e.message);}finally{setExtracting(false);}
  };
  const importAll=()=>{
    const newExp=extracted.filter(r=>r.type!=='credit').map(r=>({id:Date.now()+Math.random(),date:r.date,description:r.description,amount:Math.abs(parseFloat(r.amount)||0),category:r.category||'Miscellaneous',vendor:bankName,payment_method:'Bank',source:'AI_BANK',ref:r.description?.slice(0,30)}));
    upd('expenses')([...arr(fin.expenses),...newExp]);
    setMsg(`✓ ${newExp.length} expenses saved to database!`);setExtracted([]);setBankText('');
  };
  return(
    <div>
      <div style={S.card()}>
        <div style={S.hdr}>🤖 AI BANK STATEMENT EXTRACTION</div>
        <MsgBar msg={msg} type={msg.startsWith('✓')?'ok':'err'}/>
        <div style={S.grid('repeat(2,1fr)')}>
          <Fld label="Bank Name"><input style={S.inp} value={bankName} onChange={e=>setBankName(e.target.value)} placeholder="e.g. HDFC, SBI"/></Fld>
        </div>
        <textarea style={{...S.inp,marginTop:'10px',minHeight:'150px',fontFamily:'monospace',fontSize:'0.75rem',resize:'vertical'}}
          placeholder="Paste bank statement text here..." value={bankText} onChange={e=>setBankText(e.target.value)}/>
        <div style={{display:'flex',gap:'10px',marginTop:'10px'}}>
          <button style={S.btn('#7c3aed')} onClick={extract} disabled={extracting}>{extracting?'⟳ Extracting…':'🤖 Extract'}</button>
          <button style={S.btn('#f0f1f4','#323338')} onClick={()=>{setBankText('');setExtracted([]);setMsg('');}}>Clear</button>
        </div>
      </div>
      {extracted.length>0&&(
        <div style={S.card()}>
          <div style={S.hdr}><span>REVIEW ({extracted.length})</span><button style={S.btn('#00a65e')} onClick={importAll}>✓ Import All to DB</button></div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Date','Description','Amount','Type','Category'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{extracted.map((r,i)=>(
              <tr key={i} style={{background:r.type==='credit'?'#f0fdf4':'white'}}>
                <td style={S.TD}>{r.date}</td><td style={S.TD}>{r.description}</td>
                <td style={{...S.TD,fontWeight:'bold',color:r.type==='credit'?'#00a65e':'#e2445c',textAlign:'right'}}>{fmtC(Math.abs(r.amount))}</td>
                <td style={S.TD}><span style={{fontSize:'0.68rem',padding:'2px 7px',borderRadius:'8px',background:r.type==='credit'?'#dcfce7':'#fee2e2',color:r.type==='credit'?'#166534':'#991b1b'}}>{r.type}</span></td>
                <td style={S.TD}><select style={{...S.sel,padding:'3px 6px',fontSize:'0.72rem'}} value={r.category} onChange={e=>{const u=[...extracted];u[i]={...u[i],category:e.target.value};setExtracted(u);}}>
                  {EXPENSE_CATS.map(c=><option key={c}>{c}</option>)}</select></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── P&L REPORTS ───────────────────────────────────────────────────────────────
function Reports({pl,fin}){
  const [rep,setRep]=useState('pl');
  const monthly=useMemo(()=>{
    const m={};
    const add=(k,cb)=>{if(!k)return;if(!m[k])m[k]={month:k,revenue:0,expenses:0,salaries:0};cb(m[k]);};
    arr(fin.invoices).forEach(i=>add((i.date||'').slice(0,7),r=>{r.revenue+=parseFloat(i.amount_usd||0);}));
    arr(fin.expenses).forEach(e=>add((e.date||'').slice(0,7),r=>{r.expenses+=parseFloat(e.amount||0);}));
    arr(fin.salaries).forEach(s=>add((s.month||'').slice(0,7),r=>{r.salaries+=parseFloat(s.net||0);}));
    return Object.values(m).sort((a,b)=>a.month.localeCompare(b.month));
  },[fin.invoices,fin.expenses,fin.salaries]);

  const printPL=()=>{
    const w=window.open('','_blank','width=820,height=700');
    w.document.write(`<html><head><title>P&L — Nirvikalp</title><style>body{font-family:sans-serif;padding:30px}table{width:100%;border-collapse:collapse}td{padding:5px 10px;border-bottom:1px solid #eee}.tot td{font-weight:bold;border-top:2px solid #333;border-bottom:2px solid #333;}</style></head><body>
    <h2>PROFIT & LOSS — Nirvikalp</h2><table>
    <tr><td>Total Revenue</td><td align=right style="color:#00a65e;font-weight:bold">${fmtC(pl.totalRevenue)}</td></tr>
    <tr><td>Cost of Goods Sold</td><td align=right style="color:#e2445c">${fmtC(pl.cogs)}</td></tr>
    <tr class=tot><td>GROSS PROFIT</td><td align=right style="color:${pl.grossProfit>=0?'#00a65e':'#e2445c'}">${fmtC(pl.grossProfit)}</td></tr>
    <tr><td>Operating Expenses</td><td align=right style="color:#e2445c">${fmtC(pl.opex)}</td></tr>
    <tr><td>Depreciation</td><td align=right style="color:#f59e0b">${fmtC(pl.depn)}</td></tr>
    <tr><td>Loan Interest</td><td align=right style="color:#f59e0b">${fmtC(pl.loanInt)}</td></tr>
    <tr class=tot><td>EBIT</td><td align=right style="color:${pl.ebit>=0?'#00a65e':'#e2445c'}">${fmtC(pl.ebit)}</td></tr>
    <tr><td>Taxation</td><td align=right style="color:#e2445c">${fmtC(pl.taxPaid)}</td></tr>
    <tr class=tot><td>NET PROFIT / (LOSS)</td><td align=right style="color:${pl.netProfit>=0?'#00a65e':'#e2445c'};font-size:1.1em;font-weight:bold">${fmtC(pl.netProfit)}</td></tr>
    <tr><td colspan=2 style="color:#676879;font-size:0.85em">GP: ${fmt(pl.gpMargin,1)}% | NP: ${fmt(pl.npMargin,1)}%</td></tr>
    </table></body></html>`);w.document.close();
  };

  return(
    <div>
      <div style={{display:'flex',gap:'10px',marginBottom:'14px',alignItems:'center'}}>
        {[['pl','P&L Statement'],['monthly','Monthly Statements'],['receivables','Receivables Report']].map(([id,l])=>(
          <button key={id} style={S.tab(rep===id)} onClick={()=>setRep(id)}>{l}</button>
        ))}
        <button style={{...S.btn('#323338'),marginLeft:'auto'}} onClick={printPL}>🖨 Print</button>
      </div>

      {rep==='pl'&&(
        <div style={S.card()}>
          <div style={S.hdr}><span>PROFIT & LOSS STATEMENT</span><span style={{fontSize:'0.68rem',color:'#676879',fontWeight:'normal'}}>All amounts in USD</span></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
            <div>
              {[{l:'INCOME',h:true},{l:'Export / Order Sales',v:pl.exportSales,c:'#00a65e'},{l:'Misc Income',v:pl.miscInc,c:'#00a65e'},{l:'TOTAL REVENUE',v:pl.totalRevenue,tot:true,c:'#00a65e'},{l:''},
                {l:'COST OF GOODS SOLD',h:true},
                ...['Fabric & Materials','Trims & Accessories','Direct Labour','Packaging','Factory Overhead'].map(cat=>({l:'  '+cat,v:arr(fin.expenses).filter(e=>e.category===cat).reduce((s,e)=>s+parseFloat(e.amount||0),0),c:'#e2445c'})),
                {l:'TOTAL COGS',v:pl.cogs,tot:true,c:'#e2445c'},{l:''},
                {l:'GROSS PROFIT',v:pl.grossProfit,tot:true,c:pl.grossProfit>=0?'#00a65e':'#e2445c'},
              ].map((row,i)=>row.l===''?<div key={i} style={{height:'8px'}}/>:(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:row.tot?'2px solid #334155':'1px solid #f8fafc',borderTop:row.tot?'1px solid #334155':'none',fontWeight:row.tot||row.h?'800':'400',background:row.h?'#f0f1f4':'transparent',padding:row.h?'5px 8px':'4px 0'}}>
                  <span style={{fontSize:'0.8rem',color:row.h?'#0073ea':'#323338'}}>{row.l}</span>
                  {row.v!==undefined&&<span style={{fontSize:'0.8rem',fontWeight:'bold',color:row.c}}>{fmtC(row.v)}</span>}
                </div>
              ))}
            </div>
            <div>
              {[{l:'OPERATING EXPENSES',h:true},{l:'  Salaries',v:arr(fin.salaries).reduce((s,r)=>s+parseFloat(r.net||0),0),c:'#e2445c'},{l:'  Admin & OpEx',v:pl.opex,c:'#e2445c'},{l:'  Depreciation',v:pl.depn,c:'#f59e0b'},{l:'  Loan Interest',v:pl.loanInt,c:'#f59e0b'},
                {l:'TOTAL OPEX',v:pl.opex+pl.depn+pl.loanInt,tot:true,c:'#e2445c'},{l:''},
                {l:'EBIT',v:pl.ebit,tot:true,c:pl.ebit>=0?'#00a65e':'#e2445c'},{l:''},
                {l:'BELOW THE LINE',h:true},{l:'  Taxation',v:pl.taxPaid,c:'#e2445c'},{l:''},
                {l:'NET PROFIT / (LOSS)',v:pl.netProfit,tot:true,c:pl.netProfit>=0?'#00a65e':'#e2445c'},
              ].map((row,i)=>row.l===''?<div key={i} style={{height:'8px'}}/>:(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:row.tot?'2px solid #334155':'1px solid #f8fafc',borderTop:row.tot?'1px solid #334155':'none',fontWeight:row.tot||row.h?'800':'400',background:row.h?'#f0f1f4':'transparent',padding:row.h?'5px 8px':'4px 0'}}>
                  <span style={{fontSize:'0.8rem',color:row.h?'#0073ea':'#323338'}}>{row.l}</span>
                  {row.v!==undefined&&<span style={{fontSize:'0.8rem',fontWeight:'bold',color:row.c}}>{fmtC(row.v)}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {rep==='monthly'&&(
        <div style={S.card()}>
          <div style={S.hdr}>MONTHLY FINANCIAL STATEMENTS</div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Month','Revenue','Expenses','Salaries','Total Cost','Gross Profit','Margin%'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{monthly.map(m=>{const tc=m.expenses+m.salaries,gp=m.revenue-tc;return(
              <tr key={m.month}>
                <td style={{...S.TD,fontWeight:'bold'}}>{m.month}</td>
                <td style={{...S.TD,textAlign:'right',color:'#00a65e',fontWeight:'bold'}}>{fmtC(m.revenue)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#e2445c'}}>{fmtC(m.expenses)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#f59e0b'}}>{fmtC(m.salaries)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#e2445c',fontWeight:'bold'}}>{fmtC(tc)}</td>
                <td style={{...S.TD,textAlign:'right',fontWeight:'bold',color:gp>=0?'#00a65e':'#e2445c'}}>{fmtC(gp)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#7c3aed'}}>{m.revenue>0?fmt(gp/m.revenue*100,1)+'%':'—'}</td>
              </tr>);})}
            </tbody>
          </table>
        </div>
      )}

      {rep==='receivables'&&(
        <div style={S.card()}>
          <div style={S.hdr}><span>RECEIVABLES REPORT</span>
            <span style={{color:'#7c3aed',fontWeight:'bold'}}>Outstanding: {fmtC(arr(fin.invoices).reduce((s,i)=>s+(parseFloat(i.amount_usd)||0),0)-arr(fin.receipts).reduce((s,r)=>s+(parseFloat(r.amount)||0),0))}</span>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Invoice','Buyer','Date','Due Date','Amount','Received','Outstanding','Status'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>{arr(fin.invoices).map(inv=>{
              const rec=arr(fin.receipts).filter(r=>r.invoice_ref===inv.inv_no).reduce((s,r)=>s+(parseFloat(r.amount)||0),0);
              const out=parseFloat(inv.amount_usd||0)-rec;
              const od=out>0&&inv.due_date&&new Date(inv.due_date)<new Date();
              return(<tr key={inv.id} style={{background:od?'#fef2f2':out<=0?'#f0fdf4':'white'}}>
                <td style={{...S.TD,fontWeight:'bold',color:'#2563eb'}}>{inv.inv_no}</td><td style={S.TD}>{inv.buyer}</td>
                <td style={S.TD}>{inv.date}</td><td style={{...S.TD,color:od?'#e2445c':'inherit'}}>{inv.due_date}</td>
                <td style={{...S.TD,textAlign:'right',fontWeight:'bold'}}>{fmtC(inv.amount_usd)}</td>
                <td style={{...S.TD,textAlign:'right',color:'#00a65e'}}>{fmtC(rec)}</td>
                <td style={{...S.TD,textAlign:'right',fontWeight:'bold',color:out<=0?'#00a65e':od?'#e2445c':'#f59e0b'}}>{fmtC(out)}</td>
                <td style={S.TD}><span style={{padding:'2px 7px',borderRadius:'8px',fontSize:'0.68rem',fontWeight:'bold',background:out<=0?'#dcfce7':od?'#fee2e2':'#fef3c7',color:out<=0?'#166534':od?'#991b1b':'#92400e'}}>{out<=0?'Cleared':od?'OVERDUE':'Pending'}</span></td>
              </tr>);})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// BUDGET MODULE — Monthly/Yearly allocation · Dept/Cost-head budgets
//                 Approval workflow · 80% warning · Overspend block
//                 Top cost highlights · Comparative & trend analysis
// ═══════════════════════════════════════════════════════════════════════════════

const DEPTS = ['Production','Admin','Quality','Merchandising','Finance','IT','HR','Marketing','Operations'];
const BUDGET_PERIODS = ['Monthly','Quarterly','Yearly'];

function BudgetBar({ allocated, spent }) {
  const pct  = allocated > 0 ? Math.min((spent / allocated) * 100, 100) : 0;
  const over = spent > allocated;
  const warn = !over && pct >= 80;
  const col  = over ? '#e2445c' : warn ? '#f59e0b' : '#0073ea';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.7rem', marginBottom:'3px', color:'#676879' }}>
        <span>Spent: <strong style={{ color:col }}>{fmtC(spent)}</strong></span>
        <span>{fmt(pct,1)}% of {fmtC(allocated)}</span>
      </div>
      <div style={{ height:'8px', background:'#f0f1f4', borderRadius:'4px', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:'4px', transition:'width 0.4s' }}/>
      </div>
      {over && <div style={{ fontSize:'0.68rem', color:'#e2445c', marginTop:'3px', fontWeight:'bold' }}>🚫 Budget exceeded by {fmtC(spent - allocated)}</div>}
      {warn && <div style={{ fontSize:'0.68rem', color:'#f59e0b', marginTop:'3px', fontWeight:'bold' }}>⚠ 80% of budget consumed — approval required for further spend</div>}
    </div>
  );
}

export function Budget({ fin, upd }) {
  const [bSub, setBSub] = useState('overview');
  const budgets = useMemo(() => arr(fin.budgets), [fin.budgets]);
  const payReqs = useMemo(() => arr(fin.paymentRequests), [fin.paymentRequests]);
  const updBudgets = upd('budgets');
  const updPayReqs = upd('paymentRequests');

  // Pre-compute expense aggregation once — keyed by "year|month|dept|category"
  // Avoids repeated filter+reduce in loops inside child components
  const expenseIndex = useMemo(() => {
    const idx = {};
    arr(fin.expenses).forEach(e => {
      const yr  = (e.date||'').slice(0,4);
      const mo  = (e.date||'').slice(0,7);
      const dep = e.department || e.dept || '';
      const cat = e.category   || '';
      const amt = parseFloat(e.amount)||0;
      // By year+dept+cat
      const yk = `${yr}||${dep}||${cat}`;
      idx[yk] = (idx[yk]||0) + amt;
      // By month+dept+cat
      const mk = `${mo}||${dep}||${cat}`;
      idx[mk] = (idx[mk]||0) + amt;
      // All-dept aggregates
      const yak = `${yr}||||${cat}`;
      idx[yak] = (idx[yak]||0) + amt;
      const mak = `${mo}||||${cat}`;
      idx[mak] = (idx[mak]||0) + amt;
    });
    return idx;
  }, [fin.expenses]);

  const getSpent = useCallback((period, dept, category, periodKey) => {
    const dep = dept || '';
    const cat = category || '';
    const key = `${periodKey}||${dep}||${cat}`;
    return expenseIndex[key] || 0;
  }, [expenseIndex]);

  const BSUBS = [
    ['overview','📋 Overview'],['setup','⚙ Setup'],
    ['requests','✅ Approvals'],['analysis','📈 Cost Analysis'],
  ];
  return (
    <div>
      <div style={{ display:'flex', gap:'6px', marginBottom:'16px' }}>
        {BSUBS.map(([id,l]) => <button key={id} style={S.tab(bSub===id)} onClick={() => setBSub(id)}>{l}</button>)}
      </div>
      {bSub==='overview'  && <BudgetOverview  budgets={budgets} payReqs={payReqs} getSpent={getSpent}/>}
      {bSub==='setup'     && <BudgetSetup     budgets={budgets} updBudgets={updBudgets}/>}
      {bSub==='requests'  && <PaymentApprovals payReqs={payReqs} budgets={budgets} updPayReqs={updPayReqs} fin={fin} upd={upd} getSpent={getSpent}/>}
      {bSub==='analysis'  && <CostAnalysis    fin={fin} budgets={budgets} getSpent={getSpent}/>}
    </div>
  );
}

// ── 1. BUDGET OVERVIEW ────────────────────────────────────────────────────────
function BudgetOverview({ budgets, payReqs, getSpent }) {
  const [fPeriod, setFPeriod] = useState('');
  const [fDept,   setFDept]   = useState('');
  const [fYear,   setFYear]   = useState(new Date().getFullYear().toString());
  const curMonth = today().slice(0,7);

  const shown = budgets.filter(b =>
    (!fPeriod || b.period===fPeriod) && (!fDept || b.dept===fDept)
  );
  const pending = payReqs.filter(r=>r.status==='Pending').length;
  const totalAlloc = shown.reduce((s,b) => s+(parseFloat(b.amount)||0), 0);
  const totalSpent = shown.reduce((s,b) => {
    const pk = b.period==='Monthly' ? curMonth : fYear;
    return s + getSpent(b.period, b.dept, b.category, pk);
  }, 0);

  return (
    <div>
      <div style={S.grid('repeat(4,1fr)')}>
        {[
          ['Total Budgeted', fmtC(totalAlloc), '#0073ea'],
          ['Total Spent',    fmtC(totalSpent), totalSpent>totalAlloc?'#e2445c':'#00a65e'],
          ['Remaining',      fmtC(Math.max(totalAlloc-totalSpent,0)), '#7c3aed'],
          ['Pending Approvals', pending+' requests', '#f59e0b'],
        ].map(([l,v,c]) => (
          <div key={l} style={S.kpi(c)}>
            <div style={{ fontSize:'0.68rem', fontWeight:'bold', color:'#676879', textTransform:'uppercase' }}>{l}</div>
            <div style={{ fontSize:'1.15rem', fontWeight:'900', color:c, marginTop:'4px' }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ ...S.card(), padding:'14px 20px' }}>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Period">
            <select style={S.sel} value={fPeriod} onChange={e=>setFPeriod(e.target.value)}>
              <option value="">All Periods</option>
              {BUDGET_PERIODS.map(p=><option key={p}>{p}</option>)}
            </select>
          </Fld>
          <Fld label="Department">
            <select style={S.sel} value={fDept} onChange={e=>setFDept(e.target.value)}>
              <option value="">All Departments</option>
              {DEPTS.map(d=><option key={d}>{d}</option>)}
            </select>
          </Fld>
          <Fld label="Year / Month">
            <input style={S.inp} value={fYear} onChange={e=>setFYear(e.target.value)} placeholder="2026 or 2026-06"/>
          </Fld>
          <Fld label=" ">
            <button style={{ ...S.btn('#f0f1f4','#323338'), marginTop:'20px', width:'100%' }}
              onClick={()=>{setFPeriod('');setFDept('');setFYear(new Date().getFullYear().toString());}}>
              Clear
            </button>
          </Fld>
        </div>
      </div>
      {shown.length===0 && (
        <div style={{ ...S.card(), textAlign:'center', color:'#676879', padding:'40px' }}>
          No budgets found. Go to ⚙ Setup to create allocations.
        </div>
      )}
      <div style={S.grid('repeat(2,1fr)')}>
        {shown.map(b => {
          const pk    = b.period==='Monthly' ? curMonth : fYear;
          const spent = getSpent(b.period, b.dept, b.category, pk);
          const alloc = parseFloat(b.amount)||0;
          const pct   = alloc>0?(spent/alloc)*100:0;
          const over  = spent>alloc;
          const warn  = !over && pct>=80;
          const linked= payReqs.filter(r=>r.budget_id===b.id&&r.status==='Pending');
          return (
            <div key={b.id} style={{ ...S.card(over?'#e2445c':warn?'#f59e0b':'#e6e9ef'), borderLeft:`4px solid ${over?'#e2445c':warn?'#f59e0b':'#0073ea'}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                <div>
                  <div style={{ fontWeight:'bold', fontSize:'0.9rem' }}>{b.category}</div>
                  <div style={{ fontSize:'0.72rem', color:'#676879', marginTop:'2px' }}>{b.dept} · {b.period}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontWeight:'900', fontSize:'0.95rem', color:over?'#e2445c':warn?'#f59e0b':'#0073ea' }}>{fmtC(alloc)}</div>
                  <div style={{ fontSize:'0.68rem', color:'#676879' }}>allocated</div>
                </div>
              </div>
              <BudgetBar allocated={alloc} spent={spent}/>
              {linked.length>0&&(
                <div style={{ marginTop:'8px', padding:'6px 10px', background:'#fef3c7', borderRadius:'6px', fontSize:'0.72rem', color:'#92400e' }}>
                  ⏳ {linked.length} payment request(s) awaiting approval
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 2. BUDGET SETUP ───────────────────────────────────────────────────────────
function BudgetSetup({ budgets, updBudgets }) {
  const [form, setForm] = useState({ period:'Monthly', dept:'Production', category:'Fabric & Materials', year:today().slice(0,7) });
  const [editId, setEditId] = useState(null);
  const F  = k => form[k]||'';
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = () => {
    if(!F('amount')||parseFloat(F('amount'))<=0) return;
    const entry = { ...form, amount:parseFloat(form.amount) };
    if(editId) { updBudgets(budgets.map(b=>b.id===editId?{...b,...entry}:b)); setEditId(null); }
    else        { updBudgets([...budgets,{id:Date.now(),created:today(),...entry}]); }
    setForm({ period:'Monthly', dept:'Production', category:'Fabric & Materials', year:today().slice(0,7) });
  };

  return (
    <div>
      <div style={S.card()}>
        <div style={S.hdr}>{editId?'✏ EDIT BUDGET':'➕ CREATE BUDGET ALLOCATION'}</div>
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Period">
            <select style={S.sel} value={F('period')} onChange={e=>sf('period',e.target.value)}>
              {BUDGET_PERIODS.map(p=><option key={p}>{p}</option>)}
            </select>
          </Fld>
          <Fld label="Department">
            <select style={S.sel} value={F('dept')} onChange={e=>sf('dept',e.target.value)}>
              {DEPTS.map(d=><option key={d}>{d}</option>)}
            </select>
          </Fld>
          <Fld label="Cost Head">
            <select style={S.sel} value={F('category')} onChange={e=>sf('category',e.target.value)}>
              {EXPENSE_CATS.map(c=><option key={c}>{c}</option>)}
            </select>
          </Fld>
          <Fld label="Allocated Amount (USD)">
            <input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)} placeholder="0.00"/>
          </Fld>
          <Fld label={F('period')==='Monthly'?'Month (YYYY-MM)':'Year (YYYY)'}>
            <input style={S.inp} value={F('year')} onChange={e=>sf('year',e.target.value)}/>
          </Fld>
          <Fld label="Approval Threshold (USD)">
            <input type="number" style={S.inp} value={F('approval_threshold')||''} onChange={e=>sf('approval_threshold',e.target.value)} placeholder="e.g. 500 — requests above this need approval"/>
          </Fld>
          <Fld label="Notes">
            <input style={S.inp} value={F('notes')} onChange={e=>sf('notes',e.target.value)}/>
          </Fld>
          <Fld label=" ">
            <div style={{ display:'flex', gap:'8px', marginTop:'20px' }}>
              <button style={{ ...S.btn(editId?'#f59e0b':'#0073ea'), flex:1 }} onClick={save}>{editId?'✓ Update':'+ Add Budget'}</button>
              {editId&&<button style={S.btn('#f0f1f4','#323338')} onClick={()=>setEditId(null)}>Cancel</button>}
            </div>
          </Fld>
        </div>
      </div>
      <div style={S.card()}>
        <div style={S.hdr}>ALL BUDGET ALLOCATIONS ({budgets.length})</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['Period','Department','Cost Head','Year/Month','Allocated','Approval Threshold','Notes','Actions'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>
              {budgets.map(b=>(
                <tr key={b.id}>
                  <td style={S.TD}><span style={{ padding:'2px 8px', borderRadius:'8px', fontSize:'0.68rem', fontWeight:'bold', background:b.period==='Monthly'?'#dbeafe':b.period==='Quarterly'?'#fef3c7':'#dcfce7', color:b.period==='Monthly'?'#1e40af':b.period==='Quarterly'?'#92400e':'#166534' }}>{b.period}</span></td>
                  <td style={{ ...S.TD, fontWeight:'bold' }}>{b.dept}</td>
                  <td style={S.TD}>{b.category}</td>
                  <td style={S.TD}>{b.year}</td>
                  <td style={{ ...S.TD, fontWeight:'bold', color:'#0073ea', textAlign:'right' }}>{fmtC(b.amount)}</td>
                  <td style={{ ...S.TD, textAlign:'right' }}>{b.approval_threshold?fmtC(b.approval_threshold):'—'}</td>
                  <td style={{ ...S.TD, color:'#676879', fontSize:'0.78rem' }}>{b.notes||'—'}</td>
                  <td style={S.TD}>
                    <div style={{ display:'flex', gap:'5px' }}>
                      <button onClick={()=>{setForm({...b});setEditId(b.id);}} style={{ ...S.btn('#f0f1f4','#323338'), padding:'4px 10px', fontSize:'0.72rem' }}>✏</button>
                      <button onClick={()=>updBudgets(budgets.filter(x=>x.id!==b.id))} style={{ ...S.btn('#fee2e2','#e2445c'), padding:'4px 10px', fontSize:'0.72rem' }}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 3. PAYMENT APPROVAL WORKFLOW ──────────────────────────────────────────────
function PaymentApprovals({ payReqs, budgets, updPayReqs, fin, upd, getSpent }) {
  const [form, setForm] = useState({ date:today(), payment_method:'Bank', category:'Miscellaneous', dept:'Production' });
  const [msg, setMsg]   = useState('');
  const [mType,setMType]= useState('ok');
  const [fStatus,setFStatus]=useState('');
  const F  = k => form[k]||'';
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));
  const flash = (m,t='ok') => { setMsg(m); setMType(t); setTimeout(()=>setMsg(''),5000); };

  const findBudget = (dept,cat) => budgets.find(b=>b.dept===dept&&b.category===cat)||null;

  const submit = () => {
    if(!F('description')||!F('amount')||parseFloat(F('amount'))<=0){ flash('Enter description and amount.','err'); return; }
    const budget = findBudget(F('dept'),F('category'));
    const amt    = parseFloat(F('amount'));
    const curMonth = today().slice(0,7);

    if(budget){
      const alloc  = parseFloat(budget.amount)||0;
      const pk     = budget.period==='Monthly' ? curMonth : budget.year;
      const spent  = getSpent(budget.period,budget.dept,budget.category,pk);
      const thresh = parseFloat(budget.approval_threshold||0);

      // HARD BLOCK: would exceed budget
      if(spent+amt > alloc){
        flash(`🚫 BLOCKED — This payment (${fmtC(amt)}) would exceed the ${budget.category} budget for ${budget.dept}. Remaining: ${fmtC(alloc-spent)}.`,'err');
        return;
      }
      // WARNING: 80%+ consumed
      if((spent/alloc)>=0.8){
        flash(`⚠ 80% of ${budget.category} budget consumed (${fmtC(spent)} / ${fmtC(alloc)}). Request queued for approval.`,'warn');
      }
      // APPROVAL REQUIRED: above threshold
      if(thresh>0 && amt>thresh){
        updPayReqs([...payReqs,{id:Date.now(),created:today(),...form,amount:amt,status:'Pending',budget_id:budget.id,approval_level:'L1 – Manager'}]);
        flash(`Request for ${fmtC(amt)} queued for L1 approval (threshold: ${fmtC(thresh)}).`,'ok');
        setForm({date:today(),payment_method:'Bank',category:'Miscellaneous',dept:'Production'});
        return;
      }
    }
    // Book directly
    upd('expenses')([...arr(fin.expenses),{id:Date.now(),source:'DIRECT',...form,amount:amt}]);
    flash(`✓ Payment of ${fmtC(amt)} booked directly to expenses.`,'ok');
    setForm({date:today(),payment_method:'Bank',category:'Miscellaneous',dept:'Production'});
  };

  const LEVELS = ['L1 – Manager','L2 – Finance Head','L3 – Director'];
  const approve = req => {
    const nextIdx = LEVELS.indexOf(req.approval_level)+1;
    if(nextIdx<LEVELS.length){
      updPayReqs(payReqs.map(r=>r.id===req.id?{...r,approval_level:LEVELS[nextIdx],[`ack_${req.approval_level}`]:today()}:r));
    } else {
      updPayReqs(payReqs.map(r=>r.id===req.id?{...r,status:'Approved',approved_at:today()}:r));
      upd('expenses')([...arr(fin.expenses),{id:Date.now(),source:'APPROVED',...req,amount:parseFloat(req.amount)}]);
    }
  };
  const reject = req => updPayReqs(payReqs.map(r=>r.id===req.id?{...r,status:'Rejected',rejected_at:today()}:r));

  const shown  = payReqs.filter(r=>!fStatus||r.status===fStatus);
  const pending  = payReqs.filter(r=>r.status==='Pending').length;
  const approved = payReqs.filter(r=>r.status==='Approved').length;
  const rejected = payReqs.filter(r=>r.status==='Rejected').length;

  return (
    <div>
      <div style={S.card()}>
        <div style={S.hdr}>💳 SUBMIT PAYMENT REQUEST</div>
        {msg&&<div style={{ background:mType==='ok'?'#f0fdf4':mType==='warn'?'#fffbeb':'#fef2f2', border:`1px solid ${mType==='ok'?'#86efac':mType==='warn'?'#fcd34d':'#fca5a5'}`, borderRadius:'6px', padding:'10px 14px', marginBottom:'12px', fontSize:'0.82rem', color:mType==='ok'?'#166534':mType==='warn'?'#92400e':'#991b1b', fontWeight:'bold' }}>{msg}</div>}
        <div style={S.grid('repeat(4,1fr)')}>
          <Fld label="Date"><input type="date" style={S.inp} value={F('date')} onChange={e=>sf('date',e.target.value)}/></Fld>
          <Fld label="Department">
            <select style={S.sel} value={F('dept')} onChange={e=>sf('dept',e.target.value)}>
              {DEPTS.map(d=><option key={d}>{d}</option>)}
            </select>
          </Fld>
          <Fld label="Cost Head">
            <select style={S.sel} value={F('category')} onChange={e=>sf('category',e.target.value)}>
              {EXPENSE_CATS.map(c=><option key={c}>{c}</option>)}
            </select>
          </Fld>
          <Fld label="Amount (USD)"><input type="number" style={S.inp} value={F('amount')} onChange={e=>sf('amount',e.target.value)}/></Fld>
          <Fld label="Description" span={2}><input style={S.inp} value={F('description')} onChange={e=>sf('description',e.target.value)} placeholder="What is this payment for?"/></Fld>
          <Fld label="Vendor"><input style={S.inp} value={F('vendor')} onChange={e=>sf('vendor',e.target.value)}/></Fld>
          <Fld label="Payment Method">
            <select style={S.sel} value={F('payment_method')} onChange={e=>sf('payment_method',e.target.value)}>
              {['Bank','Cash','Cheque','UPI','SWIFT'].map(m=><option key={m}>{m}</option>)}
            </select>
          </Fld>
          <Fld label=" " span={2}><button style={{ ...S.btn('#0073ea'), marginTop:'20px', width:'100%' }} onClick={submit}>Submit Payment Request</button></Fld>
        </div>
        <div style={{ marginTop:'10px', padding:'10px 14px', background:'#f0f4ff', borderRadius:'6px', fontSize:'0.75rem', color:'#3730a3' }}>
          ℹ Payments within budget &amp; below threshold → booked immediately. Above threshold → queued for approval. Exceeding budget → blocked.
        </div>
      </div>

      <div style={S.grid('repeat(3,1fr)')}>
        {[['⏳ Pending',pending,'#f59e0b'],['✅ Approved',approved,'#00a65e'],['❌ Rejected',rejected,'#e2445c']].map(([l,v,c])=>(
          <div key={l} style={S.kpi(c)}>
            <div style={{ fontSize:'0.68rem', color:'#676879' }}>{l}</div>
            <div style={{ fontSize:'1.4rem', fontWeight:'900', color:c }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={S.card()}>
        <div style={S.hdr}>
          <span>APPROVAL QUEUE</span>
          <select style={{ ...S.sel, width:'160px' }} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="">All Status</option>
            {['Pending','Approved','Rejected'].map(s=><option key={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['Date','Dept','Category','Description','Vendor','Amount','Approval Level','Status','Actions'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
            <tbody>
              {shown.map(r=>{
                const st=r.status||'Pending';
                const BG={Pending:'#fffbeb',Approved:'#f0fdf4',Rejected:'#fef2f2'};
                const FG={Pending:'#92400e',Approved:'#166534',Rejected:'#991b1b'};
                const BGB={Pending:'#fef3c7',Approved:'#dcfce7',Rejected:'#fee2e2'};
                return(
                  <tr key={r.id} style={{ background:BG[st] }}>
                    <td style={S.TD}>{r.date}</td>
                    <td style={{ ...S.TD, fontWeight:'bold' }}>{r.dept}</td>
                    <td style={S.TD}>{r.category}</td>
                    <td style={S.TD}>{r.description}</td>
                    <td style={S.TD}>{r.vendor||'—'}</td>
                    <td style={{ ...S.TD, fontWeight:'bold', color:'#0073ea', textAlign:'right' }}>{fmtC(r.amount)}</td>
                    <td style={S.TD}><span style={{ fontSize:'0.68rem', color:'#7c3aed', fontWeight:'bold' }}>{r.approval_level||'—'}</span></td>
                    <td style={S.TD}><span style={{ padding:'2px 8px', borderRadius:'8px', fontSize:'0.68rem', fontWeight:'bold', background:BGB[st], color:FG[st] }}>{st}</span></td>
                    <td style={S.TD}>
                      {st==='Pending'&&(
                        <div style={{ display:'flex', gap:'5px' }}>
                          <button onClick={()=>approve(r)} style={{ ...S.btn('#00a65e'), padding:'4px 8px', fontSize:'0.7rem' }}>✓ Approve</button>
                          <button onClick={()=>reject(r)}  style={{ ...S.btn('#e2445c'), padding:'4px 8px', fontSize:'0.7rem' }}>✕ Reject</button>
                        </div>
                      )}
                      {st!=='Pending'&&<span style={{ fontSize:'0.7rem', color:'#676879' }}>{r.approved_at||r.rejected_at||'—'}</span>}
                    </td>
                  </tr>
                );
              })}
              {shown.length===0&&<tr><td colSpan="9" style={{ ...S.TD, textAlign:'center', color:'#c5c7d4', padding:'30px' }}>No requests</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 4. COST ANALYSIS ─────────────────────────────────────────────────────────
function CostAnalysis({ fin, budgets, getSpent }) {
  const [fDept,      setFDept]      = useState('');
  const [fYear,      setFYear]      = useState(new Date().getFullYear().toString());
  const [cmpYear,    setCmpYear]    = useState((new Date().getFullYear()-1).toString());
  const [topN,       setTopN]       = useState(5);
  const [view,       setView]       = useState('top');

  // Single pass over expenses — builds byCat, byCatPrev, monthly simultaneously
  const { byCat, byCatPrev, monthly } = useMemo(() => {
    const catCurr={}, catPrev={}, mon={};
    arr(fin.expenses).forEach(e => {
      const yr  = (e.date||'').slice(0,4);
      const mo  = (e.date||'').slice(0,7);
      const dep = e.department || e.dept || '';
      const dOk = !fDept || dep === fDept;
      const amt = parseFloat(e.amount)||0;
      if(dOk && yr===fYear)    { catCurr[e.category]=(catCurr[e.category]||0)+amt; if(mo){mon[mo]=(mon[mo]||0)+amt;} }
      if(dOk && yr===cmpYear)  { catPrev[e.category]=(catPrev[e.category]||0)+amt; }
    });
    return {
      byCat:   Object.entries(catCurr).sort((a,b)=>b[1]-a[1]),
      byCatPrev: catPrev,
      monthly: Object.entries(mon).sort((a,b)=>a[0].localeCompare(b[0])),
    };
  }, [fin.expenses, fDept, fYear, cmpYear]);

  const totCurr  = byCat.reduce((s,[,v])=>s+v,0);
  const totPrev  = Object.values(byCatPrev).reduce((s,v)=>s+v,0);
  const maxBar   = byCat[0]?.[1]||1;
  const HEAT     = ['#e2445c','#f59e0b','#fbbf24','#34d399','#00a65e'];

  return (
    <div>
      <div style={{ ...S.card(), padding:'14px 20px' }}>
        <div style={S.grid('repeat(5,1fr)')}>
          <Fld label="Department">
            <select style={S.sel} value={fDept} onChange={e=>setFDept(e.target.value)}>
              <option value="">All Depts</option>
              {DEPTS.map(d=><option key={d}>{d}</option>)}
            </select>
          </Fld>
          <Fld label="Year (Current)"><input style={S.inp} value={fYear} onChange={e=>setFYear(e.target.value)}/></Fld>
          <Fld label="Year (Compare)"><input style={S.inp} value={cmpYear} onChange={e=>setCmpYear(e.target.value)}/></Fld>
          <Fld label="Top N">
            <select style={S.sel} value={topN} onChange={e=>setTopN(Number(e.target.value))}>
              {[3,5,8,10].map(n=><option key={n} value={n}>Top {n}</option>)}
            </select>
          </Fld>
          <Fld label="View">
            <select style={S.sel} value={view} onChange={e=>setView(e.target.value)}>
              <option value="top">🔥 Top Cost Heads</option>
              <option value="comparative">⚖ Comparative</option>
              <option value="trend">📈 Monthly Trend</option>
              <option value="vs_budget">🎯 vs Budget</option>
            </select>
          </Fld>
        </div>
      </div>

      <div style={S.grid('repeat(3,1fr)')}>
        {[
          [`Total Spend ${fYear}`,fmtC(totCurr),'#e2445c'],
          [`Total Spend ${cmpYear}`,fmtC(totPrev),'#f59e0b'],
          ['YoY Change',totPrev>0?`${(((totCurr-totPrev)/totPrev)*100).toFixed(1)}%`:'—',totCurr>totPrev?'#e2445c':'#00a65e'],
        ].map(([l,v,c])=>(
          <div key={l} style={S.kpi(c)}>
            <div style={{ fontSize:'0.68rem', color:'#676879', fontWeight:'bold', textTransform:'uppercase' }}>{l}</div>
            <div style={{ fontSize:'1.1rem', fontWeight:'900', color:c, marginTop:'4px' }}>{v}</div>
          </div>
        ))}
      </div>

      {view==='top'&&(
        <div style={S.card()}>
          <div style={S.hdr}>
            <span>🔥 TOP {topN} COST HEADS — {fYear}{fDept?` · ${fDept}`:''}</span>
            <span style={{ fontSize:'0.72rem', color:'#676879', fontWeight:'normal' }}>Total: {fmtC(totCurr)}</span>
          </div>
          {byCat.slice(0,topN).map(([cat,val],i)=>{
            const pct=totCurr>0?(val/totCurr)*100:0;
            const hc=HEAT[Math.min(i,HEAT.length-1)];
            return(
              <div key={cat} style={{ marginBottom:'14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ width:'22px', height:'22px', borderRadius:'50%', background:hc, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:'bold', flexShrink:0 }}>{i+1}</span>
                    <span style={{ fontWeight:'bold', fontSize:'0.88rem' }}>{cat}</span>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ fontWeight:'900', color:hc, fontSize:'0.92rem' }}>{fmtC(val)}</span>
                    <span style={{ fontSize:'0.7rem', color:'#676879', marginLeft:'8px' }}>{fmt(pct,1)}%</span>
                  </div>
                </div>
                <div style={{ height:'10px', background:'#f0f1f4', borderRadius:'5px', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(val/maxBar)*100}%`, background:hc, borderRadius:'5px', transition:'width 0.5s' }}/>
                </div>
              </div>
            );
          })}
          {byCat.length===0&&<div style={{ textAlign:'center', color:'#c5c7d4', padding:'30px' }}>No expenses for {fYear}.</div>}
        </div>
      )}

      {view==='comparative'&&(
        <div style={S.card()}>
          <div style={S.hdr}>⚖ COMPARATIVE — {fYear} vs {cmpYear}</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={S.TH}>Cost Head</th>
                <th style={{ ...S.TH, textAlign:'right' }}>{fYear}</th>
                <th style={{ ...S.TH, textAlign:'right' }}>{cmpYear}</th>
                <th style={{ ...S.TH, textAlign:'right' }}>Change</th>
                <th style={{ ...S.TH, textAlign:'right' }}>% Δ</th>
                <th style={S.TH}>Mini Chart</th>
              </tr></thead>
              <tbody>
                {EXPENSE_CATS.map(cat=>{
                  const cur=byCat.find(([c])=>c===cat)?.[1]||0;
                  const prv=byCatPrev[cat]||0;
                  if(!cur&&!prv) return null;
                  const diff=cur-prv;
                  const dpct=prv>0?(diff/prv)*100:(cur>0?100:0);
                  const up=diff>0;
                  return(
                    <tr key={cat}>
                      <td style={{ ...S.TD, fontWeight:'bold' }}>{cat}</td>
                      <td style={{ ...S.TD, textAlign:'right' }}>{fmtC(cur)}</td>
                      <td style={{ ...S.TD, textAlign:'right', color:'#676879' }}>{fmtC(prv)}</td>
                      <td style={{ ...S.TD, textAlign:'right', fontWeight:'bold', color:up?'#e2445c':'#00a65e' }}>{up?'+':''}{fmtC(Math.abs(diff))}</td>
                      <td style={{ ...S.TD, textAlign:'right', fontWeight:'bold', color:up?'#e2445c':'#00a65e' }}>{up?'▲':'▼'} {fmt(Math.abs(dpct),1)}%</td>
                      <td style={S.TD}>
                        <div style={{ display:'flex', gap:'3px', alignItems:'flex-end', height:'22px' }}>
                          {[prv,cur].map((v,i)=>(
                            <div key={i} style={{ flex:1, background:i===0?'#e2e8f0':up?'#fca5a5':'#86efac', borderRadius:'2px 2px 0 0', height:`${Math.max((v/Math.max(cur,prv,1))*22,3)}px` }}/>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                }).filter(Boolean)}
              </tbody>
              <tfoot>
                <tr style={{ background:'#f0f1f4' }}>
                  <td style={{ ...S.TD, fontWeight:'900' }}>TOTAL</td>
                  <td style={{ ...S.TD, textAlign:'right', fontWeight:'900', color:'#e2445c' }}>{fmtC(totCurr)}</td>
                  <td style={{ ...S.TD, textAlign:'right', fontWeight:'900', color:'#676879' }}>{fmtC(totPrev)}</td>
                  <td style={{ ...S.TD, textAlign:'right', fontWeight:'900', color:totCurr>totPrev?'#e2445c':'#00a65e' }}>{totCurr>totPrev?'+':''}{fmtC(Math.abs(totCurr-totPrev))}</td>
                  <td style={{ ...S.TD, textAlign:'right', fontWeight:'900', color:totCurr>totPrev?'#e2445c':'#00a65e' }}>{totPrev>0?`${totCurr>totPrev?'▲':'▼'} ${fmt(Math.abs((totCurr-totPrev)/totPrev*100),1)}%`:'—'}</td>
                  <td style={S.TD}/>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {view==='trend'&&(
        <div style={S.card()}>
          <div style={S.hdr}>📈 MONTHLY SPEND TREND — {fYear}</div>
          {monthly.length===0
            ? <div style={{ textAlign:'center', color:'#c5c7d4', padding:'30px' }}>No data for {fYear}.</div>
            : (
              <>
                <div style={{ display:'flex', alignItems:'flex-end', gap:'6px', height:'180px', marginBottom:'8px', padding:'0 4px' }}>
                  {monthly.map(([month,val])=>{
                    const maxVal=Math.max(...monthly.map(([,v])=>v),1);
                    const isMax=val===maxVal;
                    return(
                      <div key={month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'3px' }}>
                        <div style={{ fontSize:'0.6rem', color:isMax?'#e2445c':'#676879', fontWeight:isMax?'bold':'normal' }}>{fmtC(val).replace('USD ','')}</div>
                        <div style={{ width:'100%', height:`${Math.max((val/maxVal)*140,6)}px`, background:isMax?'#e2445c':'#0073ea', borderRadius:'3px 3px 0 0', transition:'height 0.4s' }}/>
                        <div style={{ fontSize:'0.6rem', color:'#676879', whiteSpace:'nowrap' }}>{month.slice(5)}</div>
                      </div>
                    );
                  })}
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse', marginTop:'12px' }}>
                  <thead><tr>{['Month','Spend','% of Year','vs Prior Month'].map(h=><th key={h} style={S.TH}>{h}</th>)}</tr></thead>
                  <tbody>
                    {monthly.map(([month,val],i)=>{
                      const prv=i>0?monthly[i-1][1]:0;
                      const diff=val-prv;
                      return(
                        <tr key={month}>
                          <td style={{ ...S.TD, fontWeight:'bold' }}>{month}</td>
                          <td style={{ ...S.TD, fontWeight:'bold', textAlign:'right', color:'#e2445c' }}>{fmtC(val)}</td>
                          <td style={{ ...S.TD, textAlign:'right' }}>{totCurr>0?fmt(val/totCurr*100,1):0}%</td>
                          <td style={{ ...S.TD, textAlign:'right', fontWeight:'bold', color:diff>0?'#e2445c':diff<0?'#00a65e':'#676879' }}>
                            {i===0?'—':`${diff>0?'▲':diff<0?'▼':'='} ${fmtC(Math.abs(diff))}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )
          }
        </div>
      )}

      {view==='vs_budget'&&(
        <div style={S.card()}>
          <div style={S.hdr}>🎯 ACTUAL vs BUDGET — {fYear}</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={S.TH}>Dept</th><th style={S.TH}>Cost Head</th>
                <th style={{ ...S.TH, textAlign:'right' }}>Budget</th>
                <th style={{ ...S.TH, textAlign:'right' }}>Actual</th>
                <th style={{ ...S.TH, textAlign:'right' }}>Variance</th>
                <th style={S.TH}>Utilisation</th>
                <th style={S.TH}>Status</th>
              </tr></thead>
              <tbody>
                {budgets.filter(b=>!fDept||b.dept===fDept).map(b=>{
                  const pk    = b.period==='Monthly'?today().slice(0,7):fYear;
                  const spent = getSpent(b.period,b.dept,b.category,pk);
                  const alloc = parseFloat(b.amount)||0;
                  const vari  = alloc-spent;
                  const pct   = alloc>0?(spent/alloc)*100:0;
                  const over  = spent>alloc;
                  const warn  = !over&&pct>=80;
                  return(
                    <tr key={b.id} style={{ background:over?'#fef2f2':warn?'#fffbeb':'white' }}>
                      <td style={{ ...S.TD, fontWeight:'bold' }}>{b.dept}</td>
                      <td style={S.TD}>{b.category}</td>
                      <td style={{ ...S.TD, textAlign:'right' }}>{fmtC(alloc)}</td>
                      <td style={{ ...S.TD, textAlign:'right', fontWeight:'bold', color:over?'#e2445c':'#323338' }}>{fmtC(spent)}</td>
                      <td style={{ ...S.TD, textAlign:'right', fontWeight:'bold', color:vari>=0?'#00a65e':'#e2445c' }}>{vari>=0?'+':''}{fmtC(Math.abs(vari))}</td>
                      <td style={{ ...S.TD, minWidth:'140px' }}><BudgetBar allocated={alloc} spent={spent}/></td>
                      <td style={S.TD}>
                        <span style={{ padding:'2px 8px', borderRadius:'8px', fontSize:'0.68rem', fontWeight:'bold',
                          background:over?'#fee2e2':warn?'#fef3c7':'#dcfce7',
                          color:over?'#991b1b':warn?'#92400e':'#166534' }}>
                          {over?'🚫 Over Budget':warn?'⚠ 80%+ Used':'✓ On Track'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {budgets.length===0&&<tr><td colSpan="7" style={{ ...S.TD, textAlign:'center', color:'#c5c7d4', padding:'30px' }}>No budgets set up yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
